import React, { useEffect, useState, useRef, useMemo, useCallback, useLayoutEffect, lazy, Suspense, startTransition } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Badge,
    Box,
    Button,
    ButtonBase,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fab,
    FormControlLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import axios from 'axios';
import SftGlobalData from '../../library/SftGlobalData';
import SaveIcon from '@mui/icons-material/Save';
import { LanguagesProvider, toLangCsv, ALL_LANGUAGE_CODES } from './WebshopLanguagesContext';
import ShopDocumentImport, { ShopDocumentImportProvider } from './ShopDocumentImport';
import { usePermissions } from '../../library/Permissions';
import { webshopModalSizing, SliderField, WEBSHOP_MAX_WIDTH_PX } from './_webshopUi';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import PaletteIcon from '@mui/icons-material/Palette';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import WebIcon from '@mui/icons-material/Web';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import GridViewIcon from '@mui/icons-material/GridView';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import GavelIcon from '@mui/icons-material/Gavel';
import LockIcon from '@mui/icons-material/Lock';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import SyncIcon from '@mui/icons-material/Sync';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import UnlayerEditor from '../../components/UnlayerEditor';
import Colors from '../../library/Colors';
import { DEFAULT_GROUPS_FILTER_CONFIG } from './style/modals/GroupsFilterModal';
import { DEFAULT_PRODUCT_CARD_CONFIG } from './style/modals/ProductCardModal';
import { DEFAULT_PRODUCT_GRID_CONFIG } from './style/modals/ProductGridModal';
// DynamicPageTab / StaticPageTab / AboutPageTab / CartPageTab are lazy-loaded
// below (heavy rich-text editors) — see the lazy() block after the imports.
import { DEFAULT_HOURS_SEPARATOR } from './data/defaults';
import { MAP_SETTINGS_DEFAULT } from './data/defaults';
import { NoWebshopState, WebshopErrorState } from './_webshopNotice';
// ImageGridConfigModalV2 is lazy-loaded below (see the lazy() block).
import i18n from '../../i18n';
import { useWebshopSearch, WebshopSearchBar } from './search/WebshopConfigSearch';
import { registerUnsavedCheck, unregisterUnsavedCheck, requestLeaveConfirmation } from '../../library/UnsavedChangesGuard';
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
    DragOverlay,
} from '@dnd-kit/core';
import {
    arrayMove,
} from '@dnd-kit/sortable';
import {
    DEFAULT_AVAILABLE,
    STYLE_COLOR_PATHS,
    readByPath,
    shopImageBase,
    STATIC_BLOCKS,
    DEFAULT_FONTS,
    DEFAULT_CONTAINER,
    DEFAULT_ACTIVE,
    DEFAULT_ACTIVE_404,
    DEFAULT_CARDS,
    cadeaubonToSettings,
    layoutCompareJson,
    computePaymentTypeOptions,
    processingToSettings,
    syncToSettings,
} from './config/configHelpers';
import { countPosIssues } from './processing/posCheck';
import { TabPanel, ConfigSaveButton } from './config/configWidgets';
import { buildStyleRenderers } from './config/styleTabRenderers';
import { DialogBody, ModalCardFlow, useModalDesign } from './_designVariant';
import ConfigCard from './style/cards/ConfigCard';
import LandingActiveBar from './pages/LandingActiveBar';
import { DroppableColumn, BlockRowOverlay } from './config/blockBoard';
import {
    DEFAULT_CAROUSEL_CONFIG, DEFAULT_IMAGE_GRID_CONFIG,
    PROCESSING_CONFIG_DEFAULT, SYNC_CONFIG_DEFAULT,
    BRANDS_CONFIG_DEFAULT, STL_CONFIG_DEFAULT, OVERIGE_CONFIG_DEFAULT,
    CADEAUBON_CONFIG_DEFAULT,
} from './config/configDefaults';
import { loadWebshopConfig } from './config/loadWebshopConfig';
import { runSaveConfig } from './config/saveWebshopConfig';
import SaveReportDialog from './config/SaveReportDialog';
import { ContentBodyNotice, EditorModeSwitch, TextBodyEditor } from './pages/ContentTextEditor';
import { serializeChipsToTemplate } from './style/cards/ProductTitleAndUrlCard';

// Lazy-load the heaviest page editors/modal so their code (rich-text / Unlayer
// editors) is fetched on demand instead of bundled into the config-screen chunk.
// Their usages are wrapped in <Suspense> below.
const DynamicPageTab        = lazy(() => import('./pages/DynamicPageTab'));
const StaticPageTab         = lazy(() => import('./pages/StaticPageTab'));
const AboutPageTab          = lazy(() => import('./pages/AboutPageTab'));
const CartPageTab           = lazy(() => import('./pages/CartPageTab'));
// Container body languages (index = editorLang; editorRefNL/EN/FR follow this order).
const CONTAINER_LANGS = ['NL', 'EN', 'FR'];
const CONTAINER_LANG_LABELS = ['NL · Nederlands', 'EN · English', 'FR · Français'];
// Cart layout default (v1 cart-detail defaults) + the compare key the dirty
// flag uses: the setting strings the save would write.
const DEFAULT_CART_LAYOUT = { lines: [[], [], []], extra: [], sections: ['products', 'totals'] };
const cartLayoutKey = (l) => (l ? `${(l.lines || []).map(serializeChipsToTemplate).join(';')}|${(l.extra || []).join(';')}|${(l.sections || []).join(',')}` : '');
const ImageGridConfigModalV2 = lazy(() => import('./pages/ImageGridConfigModalV2'));
const CarouselConfigModal   = lazy(() => import('./pages/CarouselConfigModal'));
const ImageGridConfigModal  = lazy(() => import('./pages/ImageGridConfigModal'));
const ProcessingTab         = lazy(() => import('./processing/ProcessingTab'));
const SyncSettingsTab       = lazy(() => import('./sync/SyncSettingsTab'));
const CadeaubonTab          = lazy(() => import('./features/CadeaubonTab'));
const ShopTheLookTab        = lazy(() => import('./features/ShopTheLookTab'));
const MerkenTab             = lazy(() => import('./features/MerkenTab'));
const BrandContentCard      = lazy(() => import('./features/BrandContentCard'));
const BannersTab            = lazy(() => import('./features/BannersTab'));
const OverigeTab            = lazy(() => import('./features/OverigeTab'));

// Dot next to a Verwerking sub-tab label when that tab holds an invalid POS.
const posIssueDotSx = { '& .MuiBadge-dot': { right: -8, top: 3 } };

const WebshopConfigScreen = (props) => {
    const { can } = usePermissions();
    const navigate = useNavigate();
    // Deep-link from the Verwerking shipping-costs card to the Data screen's
    // Countries tab (per-country rates are edited there). Guards unsaved edits
    // via the same confirmation the sidebar uses before leaving.
    // useCallback: passed into the memoised ProcessingTab — a fresh identity
    // per render would defeat its React.memo.
    const goToDataCountries = useCallback(async () => {
        if (!(await requestLeaveConfirmation())) return;
        navigate('/' + (props.lang || i18n.language || 'nl') + '/webshop-data?tab=countries');
    }, [props, navigate]);
    const [tabValue, setTabValue] = useState(0); // Default to "Pages" (now first tab; opens on Front page sub-tab)
    // Permission key per main config tab index (0..4). Drives both which tab
    // buttons render and which tab is allowed to be active.
    const CONFIG_TAB_PERMS = ['webshop.config.tab.pages', 'webshop.config.tab.style', 'webshop.config.tab.features', 'webshop.config.tab.processing', 'webshop.config.tab.sync'];
    // If the active tab isn't allowed for the current role, fall back to the
    // first allowed one. Re-runs when the (simulated) role changes (`can`).
    useEffect(() => {
        if (!can(CONFIG_TAB_PERMS[tabValue])) {
            const first = CONFIG_TAB_PERMS.findIndex(p => can(p));
            if (first !== -1 && first !== tabValue) setTabValue(first);
        }
    }, [tabValue, can]); // eslint-disable-line react-hooks/exhaustive-deps
    const [pagesTab, setPagesTab] = useState(0); // 0=Front page, 1=404, 2=Cart, 3=About, 4=Disclaimer, 5=Privacy, 6=Dynamic
    const [styleTab, setStyleTab]   = useState(2); // 0=Explorer, 1=Product detail, 2=Landing page (shown first, opens by default)
    const [syncTab,  setSyncTab]    = useState(0); // 0=General, 1=Field mapping, 2=Images
    const [procTab,  setProcTab]    = useState(0); // 0=General, 1=Payments, 2=Shipping, 3=Customer fields

    const [featuresTab, setFeaturesTab] = useState(0); // 0=Cadeaubon 1=Shop the look 2=Banners 3=Overige

    // Bumped after every successful save. Each "…Dirty" useMemo includes it in
    // its deps so they all recompute once post-save — otherwise mutating the
    // baseline refs (originalXxxRef.current = …) alone wouldn't change a memo's
    // state dep, leaving ref-only sections (e.g. stores) stuck showing dirty and
    // the Save button stuck active until the next edit. MUST be declared before
    // the first dirty useMemo below (else a TDZ "before initialization" error).
    const [savedTick, setSavedTick] = useState(0);

    // Navigation modal state. All fields map 1:1 to a legacy SHOW_HEADER_*
    // / CENTER_* / HEADER_FILTER_* / HEADER_TYPE / HEADER_ITEM_BREAK_AMOUNT
    // / HOME_* setting.
    const DEFAULT_NAVIGATION_CONFIG = {
        headerContent: {
            contact: true, brands: true, shopTheLook: true,
            outlet: false,
            giftVoucher: false, search: true,
            account: true, cart: true,
            searchExpandedByDefault: false,
            showHomeButton: false,   // HOME_ENABLED
            homeUrl: '',             // HOME_URL — link target for the home button above
        },
        outletMode: {
            minDiscount: 10,
            hideFromExplore: false,
        },
        layout: {
            menuStyle: 'menuRight',              // HEADER_TYPE ('menuRight' | 'menuBelow')
            actionButtonsPosition: 'end',        // HEADER_ACCOUNT_POSITION ('start' | 'end') — only when menuStyle === 'menuRight'
            centerTopHeaderButtons: false,       // CENTER_HEADER ("Center all header items horizontally")
            centerTopButtonInMiddle: false,      // CENTER_LOGO   ("Center the logo horizontally")
            alignActionButtonsRight: true,       // HEADER_FILTER_SPACE_ACTIONS
        },
        navigationMenu: {
            categoriesShown: [],
            itemsPerRow: 6,
            menuHeight: 50,
            menuTextSize: 14,
        },
        behaviour: {
            keepMenuVisibleWhileScrolling: true,
        },
    };
    const [navigationConfig, setNavigationConfig] = useState(DEFAULT_NAVIGATION_CONFIG);
    const originalNavigationConfigRef = useRef(DEFAULT_NAVIGATION_CONFIG);
    const navigationDirty = useMemo(
        () => JSON.stringify(navigationConfig) !== JSON.stringify(originalNavigationConfigRef.current),
        [navigationConfig, savedTick]
    );
    const updateNavigation = (section, field, val) => {
        setNavigationConfig(prev => ({
            ...prev,
            [section]: { ...(prev[section] || {}), [field]: val },
        }));
    };
    // Navbar filter ORDER — `category` rows of the main-filter level
    // (NAVBAR_ITEMS_TO_LIST), each with an editable order_index. Loaded via
    // GET /admin/navbar-order?group={level}; saved as PATCH per changed row.
    const [navbarOrder, setNavbarOrder] = useState([]);
    const originalNavbarOrderRef = useRef([]);
    // Last level the list was fetched for — drives the reload-on-level-switch
    // effect without refetching on every render.
    const navbarOrderLevelRef = useRef('0');
    const navbarOrderCompare = (rows) => JSON.stringify((rows || []).map(r => ({ id: r.id, o: Number(r.order_index) || 0 })));
    const navbarOrderDirty = useMemo(
        () => navbarOrderCompare(navbarOrder) !== navbarOrderCompare(originalNavbarOrderRef.current),
        [navbarOrder, savedTick]
    );
    // FILTER_STYLE (read-only here) gates which ORDER_SIDE_FILTER_BY options
    // apply: NAME_BUT_MATCH_HEADER needs the tree style, MOST_ITEMS excludes it.
    const [navFilterStyle, setNavFilterStyle] = useState('');
    // Reload the order list live when the admin switches the main-filter
    // category in the modal (the initial load happens in the big load effect).
    const navCatLevel = (navigationConfig.navigationMenu?.categoriesShown && navigationConfig.navigationMenu.categoriesShown[0]) || '0';
    useEffect(() => {
        if (navCatLevel === navbarOrderLevelRef.current) return undefined;
        navbarOrderLevelRef.current = navCatLevel;
        if (navCatLevel === '0') {
            setNavbarOrder([]);
            originalNavbarOrderRef.current = [];
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const r = await SftGlobalData.post('webshop&action=proxy', {
                    key:        props.api,
                    webshop_id: props.selectedWebshop?.id,
                    sftUserId:  props.login?.sft_user_id,
                    user:       props.login?.login,
                    method:     'GET',
                    path:       '/admin/navbar-order',
                    query:      { group: navCatLevel },
                });
                if (cancelled) return;
                const rows = (r?.success && Array.isArray(r.data)) ? r.data : [];
                setNavbarOrder(rows);
                originalNavbarOrderRef.current = rows;
            } catch { /* keep the previous list */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navCatLevel]);

    // Footer modal state. Wiring status per section:
    //   newsletter   ↔ SHOW_NEWSLETTER / NEWSLETTER_POPUP / NEWSLETTER_POPUP_FIRST_VISIT
    //   customLinks  ↔ external_links rows (position != 'SOCIAL'), CRUD via
    //                  /admin/external-links/{id}
    //   socialMedia  ↔ external_links rows (position == 'SOCIAL'), same CRUD
    //   width        — overlap with GlobalWidthSettingsModal (canonical there)
    //   pages        ↔ SF_LINK_* (one per page slug). The URL field is an
    //                  external-link override; an empty value means "use
    //                  the internal page". The "Edit page" button on each
    //                  row navigates to the dedicated page editor in the
    //                  Pages tab (handled via `onEditPage` in the modal
    //                  JSX further down).
    //   contact      ↔ SF_INFO_NAME / _ADDRESS / _CITY / _VAT / _PHONE /
    //                  _EMAIL / _WEBSITE (all plain string settings).
    const DEFAULT_FOOTER_CONFIG = {
        // (footer width lives in globalWidths.perElement.footer — a `width`
        // field here was a never-read orphan, removed per audit A2.8)
        pages: { about: '', terms: '', privacy: '', disclaimer: '' },
        customLinks: [],
        socialMedia: [],
        contact: {
            // Backed by SF_INFO_* settings. See ContactInformationCard's
            // doc-block for the field-to-key mapping.
            shopName: '', titleSuffix: '', addressLine: '', city: '',
            vatNumber: '', phone: '', email: '', website: '',
        },
        newsletter: { enabled: false, showInPopup: false, openPopupAutomatically: false },
    };
    const [footerConfig, setFooterConfig] = useState(DEFAULT_FOOTER_CONFIG);
    const originalFooterConfigRef = useRef(DEFAULT_FOOTER_CONFIG);
    // Drop the client-side `id` field on customLinks/socialMedia entries
    // before comparing — those ids are re-generated each load and shouldn't
    // count as "dirty". Tracks serverId so re-saves don't double-POST.
    const normalizeFooterLink = (l) => ({
        serverId: l.serverId ?? null,
        url:      l.url || '',
        labels:   l.labels ? { nl: l.labels.nl || '', en: l.labels.en || '', fr: l.labels.fr || '' } : null,
    });
    const normalizeFooterForCompare = (cfg) => ({
        ...cfg,
        customLinks: (cfg.customLinks || []).map(normalizeFooterLink),
        socialMedia: (cfg.socialMedia || []).map(normalizeFooterLink),
    });
    const footerDirty = useMemo(
        () => JSON.stringify(normalizeFooterForCompare(footerConfig)) !==
              JSON.stringify(normalizeFooterForCompare(originalFooterConfigRef.current)),
        [footerConfig, savedTick]
    );
    const updateFooter = (section, field, val) => {
        // field === null signals a list replacement (customLinks / socialMedia).
        setFooterConfig(prev => field === null
            ? { ...prev, [section]: val }
            : { ...prev, [section]: { ...(prev[section] || {}), [field]: val } }
        );
    };

    // Header / Logo modal state. Image URLs are stored as plain strings; the
    // upload pipeline will land somewhere down the line — for now the cards
    // emit the file via onUploadImage and the URL is set by the parent once
    // we wire the actual upload.
    const DEFAULT_HEADER_LOGO_CONFIG = {
        size: { logoBarHeight: 100, maxContentWidth: 1500, backgroundFullWidth: true },
        images: { logoUrl: '', backgroundUrl: '', faviconUrl: '' },
    };
    const [headerLogoConfig, setHeaderLogoConfig] = useState(DEFAULT_HEADER_LOGO_CONFIG);
    const originalHeaderLogoConfigRef = useRef(DEFAULT_HEADER_LOGO_CONFIG);
    // Only `logoBarHeight` lives here (→ HEADER_HEIGHT). Max content width +
    // background stretch are edited through the shared globalWidths state
    // (perElement.header → SHOP_MAX_WIDTH_HEADER(_FW_BG)), so they're NOT part
    // of headerLogoConfig or its dirty check. Image uploads ride the save flow.
    // Image signature for the dirty check: a pending upload (has `${field}__file`)
    // reads as 'PENDING' so picking/replacing a logo/header/favicon flags dirty.
    const headerImgSig = (img = {}) => ['logoUrl', 'backgroundUrl', 'faviconUrl']
        .map(f => (img[`${f}__file`] ? 'PENDING' : (img[f] || '')))
        .join('|');
    const headerLogoDirty = useMemo(
        () => (headerLogoConfig.size?.logoBarHeight ?? 0) !==
                 (originalHeaderLogoConfigRef.current.size?.logoBarHeight ?? 0)
              || headerImgSig(headerLogoConfig.images) !== headerImgSig(originalHeaderLogoConfigRef.current.images),
        [headerLogoConfig, savedTick]
    );
    const updateHeaderLogo = (section, field, val) => {
        setHeaderLogoConfig(prev => {
            const next = { ...(prev[section] || {}), [field]: val };
            // Removing an image also drops a pending pick for that slot —
            // otherwise the save would still upload the file just removed.
            if (section === 'images' && !val) delete next[`${field}__file`];
            return { ...prev, [section]: next };
        });
    };
    const uploadHeaderImage = (field, file) => {
        // Stash a data URL for instant preview AND keep the raw File under
        // `${field}__file` so the save flow uploads it (base64 → general/{TYPE}).
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setHeaderLogoConfig(prev => ({
            ...prev,
            images: { ...(prev.images || {}), [field]: String(reader.result || ''), [`${field}__file`]: file },
        }));
        reader.readAsDataURL(file);
    };

    // Product Detail Information modal — 8 cards. Five are settings-backed:
    //   layout       ↔ PRODUCT_DETAILS_ORDER (CSV; block presence drives visibility,
    //                   CSV position drives storefront flex `order`)
    //   sectionsOpen ↔ PRODUCT_DETAILS_CONTAINER_STATES + PRODUCT_MOBILE_CONTAINER_STATES (JSON)
    //   detailsList  ↔ SHOW_FIELDS_ON_DETAIL_PAGE (CSV of 24 legacy field
    //                   keys: id, brand, name, reference, color, color_brand,
    //                   season, category_1..7, description_1..5, detail_1..5)
    //   related      ↔ PRODUCT_RELATED_ARTICLES_AMOUNT + DETAILS_COLOR_DISPLAY +
    //                   DETAILS_COLOR_DISPLAY_BORDER + DETAILS_COLOR_SHOW_OUT_OF_STOCK
    //   stock        ↔ SHOW_AVAILABLE_STOCK + SHOW_AVAILABLE_STOCK_STORES + HIDE_STORE_IF_NO_RECORD
    // The other three (descriptionTexts, titleAndUrl, breadcrumb) use
    // abstract modal field names that don't match the legacy CSV
    // vocabulary. Those stay frontend-only until the field-name translation
    // lands.
    //
    // layout.blocks shape mirrors what PageLayoutCard expects: a flat
    // `{ key: {used: bool} }` map. The `order` array preserves the CSV
    // sequence loaded from PRODUCT_DETAILS_ORDER so reorder-via-chip-DnD
    // can land later without losing the existing position.
    //
    // NICE-TO-HAVE: a 'washInstructions' block. The storefront's
    // detail.component.html only ships 8 ordered <section>s today; adding
    // wash instructions requires storefront work first, then re-adding the
    // chip in PageLayoutCard's DEFAULT_BLOCKS + here.
    const DEFAULT_PRODUCT_DETAIL_INFO_CONFIG = {
        layout: {
            blocks: {
                header:           { used: true },
                sizeSelector:     { used: true },
                addToCart:        { used: true },
                stockByLocation:  { used: true },
                variants:         { used: true },
                relatedProducts:  { used: true },
                productInfo:      { used: true },
                extraText:        { used: true },
            },
            order: ['header','sizeSelector','addToCart','stockByLocation','variants','relatedProducts','productInfo','extraText'],
        },
        sectionsOpen: {
            desktop: { productDetails: true,  variants: true,  relatedProducts: true,  articleInfo: true,  washInstructions: true,  stockByStore: false },
            mobile:  { productDetails: false, variants: false, relatedProducts: false, articleInfo: false, washInstructions: false, stockByStore: true  },
        },
        // detailsList groups mirror ProductDetailsListCard's GROUPS. Each
        // field is a legacy CSV key in SHOW_FIELDS_ON_DETAIL_PAGE; the
        // boolean is whether it's currently shown. Defaults match the
        // legacy install default: id,brand,name,reference,color,color_brand,
        // season + all category/description/detail slots on.
        detailsList: {
            identity: {
                id: true, brand: true, name: true, reference: true,
                color: true, color_brand: true, season: true,
            },
            categories: {
                category_1: true, category_2: true, category_3: true,
                category_4: true, category_5: true, category_6: true,
                category_7: true,
            },
            descriptions: {
                description_1: true, description_2: true, description_3: true,
                description_4: true, description_5: true,
                detail_1: true, detail_2: true, detail_3: true,
                detail_4: true, detail_5: true,
            },
        },
        // Mirrors the legacy model exactly: per language the POS text-type
        // code(s) shown on the product page (PRODUCT_TEXTS_LOCALE, ';'-joined
        // nl;en;fr, comma-lists allowed) and the type used for the meta
        // fields (PRODUCT_TEXTS_META_LOCALE).
        descriptionTexts: {
            nl: { page: '', meta: '' },
            en: { page: '', meta: '' },
            fr: { page: '', meta: '' },
        },
        // 4 slots backed by free-form template strings (browserTab /
        // pageHeading / pageSubheading) and a CSV (urlSlug). Each slot's
        // `used` array is a sequence of token chips ({id, key}) and text
        // chips ({id, text}); see ProductTitleAndUrlCard for parse/serialize.
        titleAndUrl: {
            browserTab:     { used: [] },
            pageHeading:    { used: [] },
            pageSubheading: { used: [] },
            urlSlug:        { used: [] },
        },
        // Breadcrumb levels are picked from brand + category_1..7. `used` is
        // the ordered list of legacy field keys; the storefront splits
        // DETAIL_PAGE_NAV_SECTION on `,` to get the same array.
        breadcrumb: { used: ['category_2', 'category_3'] },
        related: { maxToShow: 8, variantsDisplay: 'colorImage', showOutOfStock: false, highlightSelected: false },
        stock: { showStockStatus: true, showStockByStore: true, hideWhenSizeUnknown: false },
    };
    const [productDetailInfoConfig, setProductDetailInfoConfig] = useState(DEFAULT_PRODUCT_DETAIL_INFO_CONFIG);
    const originalProductDetailInfoConfigRef = useRef(DEFAULT_PRODUCT_DETAIL_INFO_CONFIG);
    // Title/URL chips carry generated `id`s that get regenerated on every
    // load (parseTemplateToChips assigns fresh ones). Strip them before
    // comparing so reordering the same fields with the same labels isn't
    // mistaken for a real edit. Each chip flattens to either its key
    // (token) or its text (literal).
    const normalizeChipForCompare = (c) =>
        c && c.text !== undefined ? `T:${c.text}` : `K:${c?.key || ''}`;
    const normalizePdiForCompare = (cfg) => {
        // descriptionTexts is wired to PRODUCT_TEXTS_LOCALE/_META_LOCALE now,
        // so it participates in the dirty compare again.
        const rest = { ...cfg };
        return {
            ...rest,
            titleAndUrl: rest.titleAndUrl ? Object.fromEntries(
                Object.entries(rest.titleAndUrl).map(([slot, slotData]) => [
                    slot,
                    { used: (slotData?.used || []).map(normalizeChipForCompare) },
                ])
            ) : {},
        };
    };
    const productDetailInfoDirty = useMemo(
        () => JSON.stringify(normalizePdiForCompare(productDetailInfoConfig)) !==
              JSON.stringify(normalizePdiForCompare(originalProductDetailInfoConfigRef.current)),
        [productDetailInfoConfig, savedTick]
    );
    const updateProductDetailInfo = (section, field, val) => {
        setProductDetailInfoConfig(prev => ({
            ...prev,
            [section]: { ...(prev[section] || {}), [field]: val },
        }));
    };

    // Carousel modal — slides are rows in the `carousel` table (CRUD via
    // /admin/carousel/*), grouped by `lang`. The Settings tab round-trips the
    // ~25 HOME_GALLERY_*/GALLERY_HOVER_* keys (per-language HOME_GALLERY_CONFIG_{L}
    // + STRETCH/OVERLAY/hover scalars). Image/video upload goes through the
    // base64-through-JSON proxy; the URL-base refs below let post-save refetches
    // hit the cheap list endpoints instead of re-pulling the whole settings dump.
    const [carouselConfig, setCarouselConfig] = useState(DEFAULT_CAROUSEL_CONFIG);
    const originalCarouselConfigRef = useRef(DEFAULT_CAROUSEL_CONFIG);
    // Image-URL prefixes captured at load time (webshop base + img_path dirs).
    // The post-save refetches use these so they can hit the cheap dedicated
    // list endpoints instead of re-pulling the whole /admin/settings dump.
    const carouselImgBaseRef = useRef('');
    const gridImgBaseRef     = useRef('');
    const layoutImgBaseRef   = useRef('');
    // Shared image-identity for the carousel/grid dirty-compares: a pending
    // upload (file) and a pending removal (× flag) each read as distinct
    // values, so both flag dirty and undoing either restores the clean state.
    const imgIdentity = (img) => {
        if (!img) return '';
        if (img.pendingRemove) return 'REMOVE:' + (img.name || '');
        if (img.file) return 'PENDING:' + img.name;
        return img.name || '';
    };
    // Normalising drops modal-only chip ids (slides get re-generated IDs on
    // load) so a JSON-compare is honest about what actually changed.
    // Settings tab: the fields that round-trip to the backend are part of
    // the compare (display/hover/behavior + text overlay via OVERLAY keys;
    // see project_carousel_modal_status memory).
    const normalizeCarouselForCompare = (cfg) => ({
        slides: Object.fromEntries(
            Object.entries(cfg.slides || {}).map(([lang, list]) => [
                lang,
                // pendingDelete rows are excluded so flagging one reads as a
                // removal (dirty flips); undo restores the clean state.
                (list || []).filter(s => !s.pendingDelete).map(s => ({
                    serverId: s.serverId ?? null,
                    order:    s.order,
                    active:   !!s.active,
                    linkUrl:  s.linkUrl  || '',
                    hoverDesktop: s.hoverDesktop || '',
                    hoverMobile:  s.hoverMobile  || '',
                    // Image identity — a pending upload (has `file`) or a
                    // pending removal (× flag) reads as a distinct value so
                    // picking/removing an image flags dirty; Herstellen (or the
                    // dialog-close flag strip) restores the clean compare.
                    desktopImage: imgIdentity(s.desktopImage),
                    altImage:     imgIdentity(s.alternativeImage),
                })),
            ])
        ),
        // Settings are PER LANGUAGE ({ nl, en, fr }) since 2026-06-04.
        settings: Object.fromEntries(['nl', 'en', 'fr'].map((cl) => {
            const ls = cfg.settings?.[cl] || {};
            // Inline overlay-device normaliser (kept local to avoid a TDZ
            // reference to the image-grid helper declared later in the file).
            const od = (d) => ({
                background: (d && d.background) || '',
                textColor:  (d && d.textColor)  || '',
                position:   (d && d.position)   || '',
                size:       Number(d && d.size)  || 0,
                width:      Number(d && d.width) || 0,
                bold:       !!(d && d.bold),
                italic:     !!(d && d.italic),
                underline:  !!(d && d.underline),
                strike:     !!(d && d.strike),
            });
            const to = ls.textOverlay || {};
            return [cl, {
                display: {
                    fullWidth:     !!ls.display?.fullWidth,
                    maxHeight:     Number(ls.display?.maxHeight) || 0,
                    maxHeightUnit: ls.display?.maxHeightUnit || 'px',
                    spacingTop:    Number(ls.display?.spacingTop) || 0,
                    spacingBottom: Number(ls.display?.spacingBottom) || 0,
                    imageFit:      ls.display?.imageFit || 'scale',
                },
                hover: {
                    effect:         ls.hover?.effect || 'none',
                    zoomLevel:      Number(ls.hover?.zoomLevel) || 0,
                    animationSpeed: Number(ls.hover?.animationSpeed) || 0,
                },
                behavior: {
                    showArrows:       !!ls.behavior?.showArrows,
                    arrowsOnHover:    !!ls.behavior?.arrowsOnHover,
                    arrowsBackground: !!ls.behavior?.arrowsBackground,
                    autoAdvance:      !!ls.behavior?.autoAdvance,
                    timePerSlide:     Number(ls.behavior?.timePerSlide) || 0,
                    transition:       ls.behavior?.transition || 'fade',
                },
                textOverlay: {
                    desktop: od(to.desktop),
                    mobile:  od(to.mobile),
                    onlyOnHover:        !!to.onlyOnHover,
                    alwaysVisible:      !!to.alwaysVisible,
                    useDesktopOnMobile: !!to.useDesktopOnMobile,
                },
            }];
        })),
    });
    const carouselDirty = useMemo(
        () => JSON.stringify(normalizeCarouselForCompare(carouselConfig)) !==
              JSON.stringify(normalizeCarouselForCompare(originalCarouselConfigRef.current)),
        [carouselConfig, savedTick]
    );

    // Image grid modal — same pattern as Carousel. Cells = rows in the
    // `image_grid` table (CRUD via /admin/image-grid/*), grouped by `lang`.
    // Settings round-trip through per-language IMAGE_GRID_CONFIG_{lang} JSON
    // (mig166); image upload uses the same base64-through-JSON proxy as carousel.
    const [imageGridConfig, setImageGridConfig] = useState(DEFAULT_IMAGE_GRID_CONFIG);
    const originalImageGridConfigRef = useRef(DEFAULT_IMAGE_GRID_CONFIG);
    // Toggle between the classic image-grid modal and the experimental V2
    // (live CSS-grid preview). State is local-only — both modals share the
    // same `imageGridConfig`, so flipping doesn't lose edits or trigger any
    // backend write. Default to classic so the existing UX is unchanged
    // until the user opts in.
    const [useImageGridV2, setUseImageGridV2] = useState(false);
    // Per-device overlay slice used by the dirty-compare. Each modal device
    // editor (desktop / mobile) carries the same field set; we coerce the
    // values so a missing key on one side doesn't false-positive as dirty.
    const normalizeOverlayDevice = (d) => ({
        background: (d && d.background) || '',
        textColor:  (d && d.textColor)  || '',
        position:   (d && d.position)   || '',
        size:       Number(d && d.size)  || 0,
        width:      Number(d && d.width) || 0,
        bold:       !!(d && d.bold),
        italic:     !!(d && d.italic),
        underline:  !!(d && d.underline),
        strike:     !!(d && d.strike),
    });
    const normalizeImageGridForCompare = (cfg) => ({
        cells: Object.fromEntries(
            Object.entries(cfg.cells || {}).map(([lang, list]) => [
                lang,
                // pendingDelete rows are excluded so flagging one reads as a
                // removal (dirty flips); undo restores the clean state.
                (list || []).filter(c => !c.pendingDelete).map(c => ({
                    serverId: c.serverId ?? null,
                    order:    c.order,
                    active:   !!c.active,
                    width:    Math.max(1, Number(c.width)  || 1),
                    height:   Math.max(1, Number(c.height) || 1),
                    linkUrl:  c.linkUrl  || '',
                    hoverDesktop: c.hoverDesktop || '',
                    hoverMobile:  c.hoverMobile  || '',
                    // Image identity — pending upload (has `file`) reads as a
                    // distinct value so picking/removing an image flags dirty.
                    desktopImage: imgIdentity(c.desktopImage),
                    altImage:     imgIdentity(c.alternativeImage),
                })),
            ])
        ),
        // Settings are PER LANGUAGE ({ nl, en, fr }) since 2026-06-04.
        settings: Object.fromEntries(['nl', 'en', 'fr'].map((gl) => {
            const ls = cfg.settings?.[gl] || {};
            return [gl, {
                layout: {
                    columns:          Number(ls.layout?.columns) || 0,
                    gap:              Number(ls.layout?.gap) || 0,
                    keepGridOnMobile: !!ls.layout?.keepGridOnMobile,
                    margin:           Array.isArray(ls.layout?.margin)
                        ? [Number(ls.layout.margin[0]) || 0, Number(ls.layout.margin[1]) || 0]
                        : [0, 0],
                },
                hover: {
                    effect:         ls.hover?.effect || 'none',
                    zoomLevel:      Number(ls.hover?.zoomLevel) || 0,
                    animationSpeed: Number(ls.hover?.animationSpeed) || 0,
                },
                // Stable shape for the textOverlay block — mirrors the dual
                // desktop+mobile structure that overlay.desktop/.mobile use in
                // IMAGE_GRID_CONFIG_{lang} JSON (mig166).
                textOverlay: {
                    desktop: normalizeOverlayDevice(ls.textOverlay?.desktop),
                    mobile:  normalizeOverlayDevice(ls.textOverlay?.mobile),
                    onlyOnHover:        !!ls.textOverlay?.onlyOnHover,
                    alwaysVisible:      !!ls.textOverlay?.alwaysVisible,
                    useDesktopOnMobile: !!ls.textOverlay?.useDesktopOnMobile,
                },
            }];
        })),
    });
    const imageGridDirty = useMemo(
        () => JSON.stringify(normalizeImageGridForCompare(imageGridConfig)) !==
              JSON.stringify(normalizeImageGridForCompare(originalImageGridConfigRef.current)),
        [imageGridConfig, savedTick]
    );

    // Product card config — held here so the Verkenner preview (product
    // grid) can read the live values (line composition, divider, alignment).
    // Backed by legacy admin settings (PRODUCT_DISPLAY_INFO, PRODUCT_*, etc.);
    // see the load effect below for the per-field mapping.
    const [productCardConfig, setProductCardConfig] = useState(DEFAULT_PRODUCT_CARD_CONFIG);
    const originalProductCardConfigRef = useRef(DEFAULT_PRODUCT_CARD_CONFIG);
    // PRODUCT_HOVER_MAX_SIZES is "Y,X" CSV (lengtematen, breedtematen). The
    // v1 hover badge only lists sizes for a product with ONE size row, and
    // caps that row at [1] — so for an ordinary S/M/L shop [1] is "the number
    // of sizes shown", the only axis the legacy admin exposed and what the
    // modal's "Maximum sizes shown" edits. [0] (rows; the legacy admin had it
    // commented out) is stashed here so save can round-trip it untouched.
    const productHoverMaxRowsRef = useRef(3);
    // Normalising drops chip ids (they're regenerated on load and don't carry
    // user intent) so a JSON compare can be honest about what actually changed.
    // Each chip flattens to either its key (token) or its text (literal).
    const normalizeChip = (c) => (c && c.text !== undefined ? `T:${c.text}` : `K:${c?.key || ''}`);
    const normalizeCardForCompare = (cfg) => ({
        productInfo: {
            line1: (cfg.productInfo?.line1 || []).map(normalizeChip),
            line2: (cfg.productInfo?.line2 || []).map(normalizeChip),
            line3: (cfg.productInfo?.line3 || []).map(normalizeChip),
            showDivider: !!cfg.productInfo?.showDivider,
            align:        cfg.productInfo?.align || 'left',
        },
        image:       cfg.image       || {},
        sizePreview: cfg.sizePreview || {},
    });
    const productCardDirty = useMemo(
        () => JSON.stringify(normalizeCardForCompare(productCardConfig)) !==
              JSON.stringify(normalizeCardForCompare(originalProductCardConfigRef.current)),
        [productCardConfig, savedTick]
    );

    // Groups & sorting config — opened from the cog on the GROEPEN sidebar
    // in the Verkenner mockup. Backed by FILTER_*, SIDE_FILTER_*, and
    // EXPLORE_SORT_* legacy settings; see the load effect for the per-key
    // mapping.
    const [groupsFilterConfig, setGroupsFilterConfig] = useState(DEFAULT_GROUPS_FILTER_CONFIG);
    const originalGroupsFilterConfigRef = useRef(DEFAULT_GROUPS_FILTER_CONFIG);
    // The sort-rule entries carry React-only `id`s — strip them before
    // comparing so reordering the same fields doesn't trigger dirty if the
    // backend mapping is identical.
    const normalizeGroupsForCompare = (cfg) => ({
        ...cfg,
        defaultSortRule: (cfg.defaultSortRule || []).map(r => `${r.field}:${r.direction}`),
    });
    const groupsFilterDirty = useMemo(
        () => JSON.stringify(normalizeGroupsForCompare(groupsFilterConfig)) !==
              JSON.stringify(normalizeGroupsForCompare(originalGroupsFilterConfigRef.current)),
        [groupsFilterConfig, savedTick]
    );

    // Style settings ref + dirty are computed after `styleSettings` itself is
    // declared (further down). The table mapping lives at module scope so it
    // can be referenced from both the load effect and the save handler.
    const originalStyleSettingsRef = useRef(null);

    // Product grid config — opened from the cog between cards 2 and 3 in
    // the Verkenner preview. Drives column count, pagination position and
    // variant grouping behaviour. Backed by mini-api settings GRID_* — the
    // load effect populates both the working state and the ref below; save
    // diffs the two so an unchanged save is a true no-op.
    const [productGridConfig, setProductGridConfig] = useState(DEFAULT_PRODUCT_GRID_CONFIG);
    const originalProductGridConfigRef = useRef(DEFAULT_PRODUCT_GRID_CONFIG);
    const productGridDirty = useMemo(
        () => JSON.stringify(productGridConfig) !== JSON.stringify(originalProductGridConfigRef.current),
        [productGridConfig, savedTick]
    );

    // Image gallery modal — flat object, single card. Default position is
    // "left" so the preview matches the schema example (3 thumbs vertical
    // column on the left of a large main image). Wired to:
    //   thumbnailPosition       ↔ GALLERY_THUMBS_POSITION (incl. 'hidden')
    //   useGrid                 ↔ DETAIL_USE_NEW_GALLERY
    //   maxThumbnails           ↔ NEW_GALLERY_THUMBS_COUNT
    //   showArrows + colors     ↔ NEW_GALLERY_CONFIG.main_arrows  (JSON)
    //   showFullscreen + colors ↔ NEW_GALLERY_CONFIG.thumb_arrows (JSON)
    const DEFAULT_IMAGE_GALLERY_CONFIG = {
        thumbnailPosition: 'left',
        useGrid: true,
        maxThumbnails: 3,
        showArrows: true,
        arrowColor: '#000000',
        arrowBgColor: '#ffffff',
        showFullscreen: true,
        fullscreenArrowColor: '#000000',
        fullscreenArrowBgColor: '#ffffff',
    };
    const [imageGalleryConfig, setImageGalleryConfig] = useState(DEFAULT_IMAGE_GALLERY_CONFIG);
    const originalImageGalleryConfigRef = useRef(DEFAULT_IMAGE_GALLERY_CONFIG);
    const imageGalleryDirty = useMemo(
        () => JSON.stringify(imageGalleryConfig) !== JSON.stringify(originalImageGalleryConfigRef.current),
        [imageGalleryConfig, savedTick]
    );
    const updateImageGallery = (field, val) => {
        setImageGalleryConfig(prev => ({ ...prev, [field]: val }));
    };

    // Extra Footer modal state. Each column independently picks its editor
    // mode (wysiwyg default, switchable to unlayer). Both content shapes
    // are kept side-by-side per column so a mode switch doesn't lose work.
    const blankColumn = () => ({
        editor: 'wysiwyg',
        html:   { nl: '', en: '', fr: '' },
        design: { nl: null, en: null, fr: null },
    });
    // Extra footer config:
    //   showPaymentMethods    ↔ FOOTER_EXTRA_PAYMENT_METHODS (bool setting)
    //   columns.{1,2,3}       ↔ contents row TEXT_FOOTER_COL_{1,2,3}, via
    //                            the /admin/contents/{attribute} PATCH
    //
    // Each column carries both a wysiwyg HTML body and an unlayer design
    // side by side so the user can switch editors without losing work.
    // Loading from the backend only sees the HTML side (body_nl/en/fr in
    // the contents row); the unlayer design lives on disk and isn't read
    // back yet — switching to unlayer after a reload starts blank.
    const DEFAULT_EXTRA_FOOTER_CONFIG = {
        showPaymentMethods: true,
        columns: { 1: blankColumn(), 2: blankColumn(), 3: blankColumn() },
    };
    const [extraFooterConfig, setExtraFooterConfig] = useState(DEFAULT_EXTRA_FOOTER_CONFIG);
    const originalExtraFooterConfigRef = useRef(DEFAULT_EXTRA_FOOTER_CONFIG);
    // Dirty compare: include the editor mode (switching unlayer → wysiwyg
    // means the next save will delete the .json sidecar files server-side)
    // AND the design content (unlayer edits typically change the design
    // before they reach the rendered HTML, so a presence-only flag would
    // miss them). Stringifying multi-KB design blobs per render is fine
    // — the modal isn't a hot path.
    const normalizeExtraFooterColumn = (col) => ({
        editor: col?.editor || 'wysiwyg',
        html:   {
            nl: col?.html?.nl || '',
            en: col?.html?.en || '',
            fr: col?.html?.fr || '',
        },
        design: {
            nl: col?.design?.nl ?? null,
            en: col?.design?.en ?? null,
            fr: col?.design?.fr ?? null,
        },
    });
    const normalizeExtraFooterForCompare = (cfg) => ({
        showPaymentMethods: !!cfg.showPaymentMethods,
        columns: {
            1: normalizeExtraFooterColumn(cfg.columns?.[1]),
            2: normalizeExtraFooterColumn(cfg.columns?.[2]),
            3: normalizeExtraFooterColumn(cfg.columns?.[3]),
        },
    });
    const extraFooterDirty = useMemo(
        () => JSON.stringify(normalizeExtraFooterForCompare(extraFooterConfig)) !==
              JSON.stringify(normalizeExtraFooterForCompare(originalExtraFooterConfigRef.current)),
        [extraFooterConfig, savedTick]
    );

    // Static text pages (Pages tab → About / Disclaimer / Privacy). Each maps
    // to a `contents` row (TEXT_ABOUT / TEXT_DISCLAIMER / TEXT_PRIVACY),
    // exposed in the settings dump as s.contents_{about,disclaimer,privacy}.
    // Same content-PATCH save path as the Extra footer columns.
    const STATIC_PAGE_DEFAULT = {
        iframe: false, editor: 'wysiwyg',
        html:    { nl: '', en: '', fr: '' },
        designs: { nl: null, en: null, fr: null },
        isCustomHtml: false,
    };
    const [staticPages, setStaticPages] = useState({
        about: STATIC_PAGE_DEFAULT, disclaimer: STATIC_PAGE_DEFAULT, privacy: STATIC_PAGE_DEFAULT,
        // page404 = the 404 page's "text" block (contents_404 / TEXT_404),
        // edited via the block's gear dialog on the Pages → 404 sub-tab.
        page404: STATIC_PAGE_DEFAULT,
        // intro = the landing page's "Welcome" block (contents_intro / TEXT_INTRO),
        // edited via that block's gear dialog on the Pages → Front page sub-tab.
        intro: STATIC_PAGE_DEFAULT,
        // detail = the "Product information" free text (contents_detail / TEXT_DETAIL)
        // shown on every product detail page — edited full-width at the bottom of
        // the Product-detail information cog modal (Opmaak).
        detail: STATIC_PAGE_DEFAULT,
    });
    const originalStaticPagesRef = useRef(staticPages);
    // Apply a StaticPageTab updater (function or object) against the latest
    // page config — lets the controlled child push functional updates without
    // stale-closure risk (the unlayer auto-sync relies on this).
    // F27: one stable setter per page key. `setStaticPage('about')` used to
    // mint a fresh closure per render, which defeats React.memo on the heavy
    // editor tabs receiving it as onChange. The body only touches the
    // functional setState, so the map never needs to invalidate.
    const staticPageSetters = useMemo(() => {
        const make = (key) => (updater) => setStaticPages(prev => ({
            ...prev,
            [key]: typeof updater === 'function' ? updater(prev[key]) : updater,
        }));
        return Object.fromEntries(
            ['about', 'disclaimer', 'privacy', 'page404', 'intro', 'detail', 'cart', 'cheques']
                .map(k => [k, make(k)]));
    }, []);
    const setStaticPage = (key) => staticPageSetters[key] || ((updater) => setStaticPages(prev => ({
        ...prev,
        [key]: typeof updater === 'function' ? updater(prev[key]) : updater,
    })));
    const staticPagesDirty = useMemo(
        () => JSON.stringify(staticPages) !== JSON.stringify(originalStaticPagesRef.current),
        [staticPages, savedTick]
    );

    // Physical retail stores (Pages tab → About). Loaded from the mini-api
    // `GET /stores` and saved per-store via `PATCH /stores/{id}`. Opening hours
    // round-trip through the legacy per-day string columns (hours_mon..sun) via
    // storeRowToForm / storeFormToColumns.
    const [stores, setStores] = useState([]);
    const originalStoresRef = useRef([]);
    const storesDirty = useMemo(
        () => JSON.stringify(stores) !== JSON.stringify(originalStoresRef.current),
        [stores, savedTick]
    );
    // Break separator for multi-range opening hours (global for the About page,
    // applied to every store on save). Detected from the loaded hours strings so
    // it round-trips without a backend key. Tracked separately from `stores`
    // because changing only the separator doesn't touch the structured hours
    // state — but it does change the strings we write, so it needs its own dirty.
    const [hoursSeparator, setHoursSeparator] = useState(DEFAULT_HOURS_SEPARATOR);
    const originalHoursSeparatorRef = useRef(DEFAULT_HOURS_SEPARATOR);
    const separatorDirty = hoursSeparator !== originalHoursSeparatorRef.current;
    // Google Maps key — the storefront and the legacy super-user admin both use
    // the `GOOGLE_MAPS_TOKEN` setting (NOT `GMAPS_API_KEY`, which the new
    // Connections tab mistakenly reads — hence it shows empty there). Read-only
    // here; the About page uses it to geocode store addresses.
    const [gmapsApiKey, setGmapsApiKey] = useState('');

    // About-page store-locations map settings. Mode/position/zoom map to the
    // ABOUT_US_MAP_* settings; the style maps to the mapStyle.json file (read
    // from the settings dump as s.mapStyle, saved via POST /admin/maps-style).
    const [mapSettings, setMapSettings] = useState(MAP_SETTINGS_DEFAULT);
    const originalMapSettingsRef = useRef(MAP_SETTINGS_DEFAULT);
    const mapSettingsDirty = useMemo(
        () => JSON.stringify(mapSettings) !== JSON.stringify(originalMapSettingsRef.current),
        [mapSettings, savedTick]
    );
    // Global width settings — centralised max-content-width values shared
    // across the storefront. Path-style updates: 'applyToWholeSite',
    // 'productPagesWidth', 'perElement.<key>.width', 'perElement.<key>.bgFullWidth'.
    const DEFAULT_GLOBAL_WIDTHS = {
        applyToWholeSite: false,
        productPagesWidth: 1500,
        // BUTTON_BORDER_RADIUS (px) — new-admin-only key (created on first
        // save via the mini-api creatable whitelist; no legacy migration).
        buttonBorderRadius: 4,
        perElement: {
            languageBar:     { width: 2500, bgFullWidth: true },
            header:          { width: 2500, bgFullWidth: true },
            infoBannerBelow: { width: 1200, bgFullWidth: true },
            infoBannerTop:   { width: 0,    bgFullWidth: true },
            brands:          { width: 1500 },
            explore:         { width: 1500 },
            recentlyViewed:  { width: 1500, bgFullWidth: true },
            footer:          { width: 1500, bgFullWidth: true },
        },
    };
    const [globalWidths, setGlobalWidths] = useState(DEFAULT_GLOBAL_WIDTHS);
    const originalGlobalWidthsRef = useRef(DEFAULT_GLOBAL_WIDTHS);
    const globalWidthsDirty = useMemo(
        () => JSON.stringify(globalWidths) !== JSON.stringify(originalGlobalWidthsRef.current),
        [globalWidths, savedTick]
    );
    const updateGlobalWidths = (field, val) => {
        if (field === 'applyToWholeSite' || field === 'productPagesWidth' || field === 'buttonBorderRadius') {
            setGlobalWidths(prev => ({ ...prev, [field]: val }));
            return;
        }
        const m = /^perElement\.([a-zA-Z]+)\.(width|bgFullWidth)$/.exec(field);
        if (m) {
            const key = m[1];
            const sub = m[2];
            setGlobalWidths(prev => ({
                ...prev,
                perElement: {
                    ...prev.perElement,
                    [key]: { ...(prev.perElement[key] || {}), [sub]: val },
                },
            }));
        }
    };

    const updateExtraFooter = (field, val) => {
        // Supported path shapes:
        //   'showPaymentMethods'
        //   'columnN.editor'
        //   'columnN.html.<lang>'
        //   'columnN.design.<lang>'
        if (field === 'showPaymentMethods') {
            setExtraFooterConfig(prev => ({ ...prev, showPaymentMethods: val }));
            return;
        }
        const editorMatch = /^column([123])\.editor$/.exec(field);
        if (editorMatch) {
            const col = editorMatch[1];
            setExtraFooterConfig(prev => ({
                ...prev,
                columns: { ...prev.columns, [col]: { ...(prev.columns[col] || blankColumn()), editor: val } },
            }));
            return;
        }
        const contentMatch = /^column([123])\.(html|design)\.(nl|en|fr)$/.exec(field);
        if (contentMatch) {
            const col   = contentMatch[1];
            const kind  = contentMatch[2];   // 'html' | 'design'
            const lang  = contentMatch[3];
            setExtraFooterConfig(prev => {
                const colState = prev.columns[col] || blankColumn();
                return {
                    ...prev,
                    columns: {
                        ...prev.columns,
                        [col]: {
                            ...colState,
                            [kind]: { ...(colState[kind] || {}), [lang]: val },
                        },
                    },
                };
            });
        }
    };
    const [available, setAvailable] = useState(DEFAULT_AVAILABLE);
    const [active, setActive] = useState(DEFAULT_ACTIVE);
    // 404-page active list + its OWN available catalog. The 404 page and the
    // Front page (landing) are independent: activating a block on one must NOT
    // remove it from the other's Available column, so each tab gets its own
    // available list (a block can sit in both). Per-entry .config stores per-tab
    // settings.
    const [active404, setActive404] = useState(DEFAULT_ACTIVE_404);
    const [available404, setAvailable404] = useState(DEFAULT_AVAILABLE);
    const [cards, setCards] = useState(DEFAULT_CARDS);
    const originalCardsRef = useRef(DEFAULT_CARDS);
    // Cart page layout (CART_DISPLAY_INFO lines as chips + CART_SECTIONS_ORDER);
    // saved with the cart cards (same tab, same save section).
    const [cartLayout, setCartLayout] = useState(DEFAULT_CART_LAYOUT);
    const originalCartLayoutRef = useRef(DEFAULT_CART_LAYOUT);
    const cardsDirty = useMemo(
        () => JSON.stringify(cards) !== JSON.stringify(originalCardsRef.current)
            || cartLayoutKey(cartLayout) !== cartLayoutKey(originalCartLayoutRef.current),
        [cards, cartLayout, savedTick]
    );
    // Product containers the cart page can show (every container lives in
    // either the landing Available or Active list; unsaved new ones excluded).
    const cartContainerOptions = useMemo(() => {
        const seen = new Map();
        [...active, ...available].forEach(b => {
            if (b && b.isContainer && /^pd_cnt-\d+$/.test(String(b.id)) && !seen.has(b.id)) seen.set(b.id, b.name);
        });
        return Array.from(seen, ([id, name]) => ({ id, name }));
    }, [active, available]);
    // Brands page (Merken). Flat keys BRANDS_ONLY_ONLINE / _CATEGORY_FILTER /
    // _PAGE_STYLE + the BRANDS_PAGE_CONFIG JSON blob (short keys lm/am/cols/max/
    // sul/d/m). originalBrandsRawJsonRef keeps the raw loaded blob so save can
    // merge over it and preserve keys the tab doesn't surface (idx, divider…).
    const [brandsConfig, setBrandsConfig] = useState(BRANDS_CONFIG_DEFAULT);
    const originalBrandsConfigRef = useRef(BRANDS_CONFIG_DEFAULT);
    const originalBrandsRawJsonRef = useRef({});
    const brandsConfigDirty = useMemo(
        () => JSON.stringify(brandsConfig) !== JSON.stringify(originalBrandsConfigRef.current),
        [brandsConfig, savedTick]
    );
    // Dynamic pages (Pages → Dynamic page). Loaded from GET /dynamic-pages,
    // saved per page via POST/PATCH/DELETE /dynamic-pages. Designs are lazy-
    // loaded into the editor via the proxy; only edited langs sit in `content`.
    const [dynamicPages, setDynamicPages] = useState([]);
    const originalDynamicPagesRef = useRef([]);
    const dynamicPagesDirty = useMemo(
        () => JSON.stringify(dynamicPages) !== JSON.stringify(originalDynamicPagesRef.current),
        [dynamicPages, savedTick]
    );
    // Information banners (Features → Banners). Loaded from the settings dump
    // (s.informationBanners includes ALL non-deleted rows, unlike the public
    // GET /information-banner which filters by active+date). Saved per-banner
    // via POST/PATCH/soft-DELETE.
    const [banners, setBanners] = useState([]);
    const originalBannersRef = useRef([]);
    // `editor` is a UI-only mode (wysiwyg/html) and isn't persisted, so it
    // shouldn't flag the screen dirty — drop it from the comparison.
    const bannerCompareJson = (arr) => JSON.stringify((arr || []).map(({ editor, ...rest }) => rest));
    const bannersDirty = useMemo(
        () => bannerCompareJson(banners) !== bannerCompareJson(originalBannersRef.current),
        [banners, savedTick]
    );

    // Shop the look (Features → Shop the look). Intro text ↔ TEXT_LOOKS content
    // row; the rest ↔ LOOKS_* / LOOK_* / SHOW_FIELDS_ON_LOOK_* settings.
    // `introEditor` is UI-only and `showInIframe`/`ctaText` have no confirmed
    // backend yet — all excluded from the dirty compare / save.
    const [looksConfig, setLooksConfig] = useState(STL_CONFIG_DEFAULT);
    const originalLooksConfigRef = useRef(STL_CONFIG_DEFAULT);
    // The look CTA button label is the shared i18n string GLOBAL.LOOKS_DETAILS;
    // we keep its i18n-table row id so the save can PATCH /localization/{id}.
    const looksCtaIdRef = useRef(null);
    const looksCompareJson = (c) => {
        if (!c) return '';
        const { introEditor, ...rest } = c;
        // title chips compare by the template the shop gets (ids are UI-only)
        return JSON.stringify({ ...rest, productTitle: serializeChipsToTemplate(rest.productTitle), productSubtitle: serializeChipsToTemplate(rest.productSubtitle) });
    };
    const looksConfigDirty = useMemo(
        () => looksCompareJson(looksConfig) !== looksCompareJson(originalLooksConfigRef.current),
        [looksConfig, savedTick]
    );

    // Overige (Features → Overige). Only the parts with a confirmed backend are
    // persisted: custom code (head/body → _scriptsHeader/_scriptsBody) and the
    // gtm/metaPixel tracking-tool ids (→ GTM_ID / PIXEL_ID). Cookie consent,
    // the ga4/tiktok/hotjar tools and the per-tool "active" flag stay in the UI
    // but aren't saved yet (pending backend), so they're excluded from dirty.
    const [overigeConfig, setOverigeConfig] = useState(OVERIGE_CONFIG_DEFAULT);
    const originalOverigeConfigRef = useRef(OVERIGE_CONFIG_DEFAULT);
    const overigePersistJson = (c) => JSON.stringify({
        head: c.customCode?.head || '',
        body: c.customCode?.body || '',
        // All 5 tracking tools persist now (id + active) via
        // POST /admin/tracking-tools — include them all in the dirty compare.
        tools: (c.trackingTools || []).map(t => ({ key: t.key, id: t.id || '', active: !!t.active, category: t.category || '' })).sort((a, b) => a.key.localeCompare(b.key)),
        cookieStyle:    c.cookies?.bannerStyle || '',
        cookiePos:      c.cookies?.position || '',
        cookieAnalytics: !!c.cookies?.categories?.analytics,
        cookieMarketing: !!c.cookies?.categories?.marketing,
        cookiePersonalization: !!c.cookies?.categories?.personalization,
    });
    const overigeConfigDirty = useMemo(
        () => overigePersistJson(overigeConfig) !== overigePersistJson(originalOverigeConfigRef.current),
        [overigeConfig, savedTick]
    );

    // Cadeaubon (Features → Cadeaubon). Settings map to the CHEQUES_* settings
    // rows; the Translations table maps to the cheques_placeholders CRUD list.
    // Voucher layouts stay UI-only (need the multipart image upload + designer).
    const [cadeaubonConfig, setCadeaubonConfig] = useState(CADEAUBON_CONFIG_DEFAULT);
    const originalCadeaubonConfigRef = useRef(CADEAUBON_CONFIG_DEFAULT);
    const cadeaubonConfigDirty = useMemo(
        () => JSON.stringify(cadeaubonToSettings(cadeaubonConfig)) !== JSON.stringify(cadeaubonToSettings(originalCadeaubonConfigRef.current)),
        [cadeaubonConfig, savedTick]
    );
    const [cadeaubonPlaceholders, setCadeaubonPlaceholders] = useState([]);
    const originalPlaceholdersRef = useRef([]);
    const placeholderCompareJson = (arr) => JSON.stringify((arr || []).map(p => ({
        id: p.id ?? null, description: p.description || '', layout: p.layout || '', nl: p.nl || '', en: p.en || '', fr: p.fr || '',
    })));
    const cadeaubonPlaceholdersDirty = useMemo(
        () => placeholderCompareJson(cadeaubonPlaceholders) !== placeholderCompareJson(originalPlaceholdersRef.current),
        [cadeaubonPlaceholders, savedTick]
    );
    // Cadeaubon voucher layouts (cheques_layouts CRUD + image uploads + Unlayer
    // design). Loaded via GET /cheques-layouts (not in the settings dump).
    const [cadeaubonLayouts, setCadeaubonLayouts] = useState([]);
    const originalCadeaubonLayoutsRef = useRef([]);
    const cadeaubonLayoutsDirty = useMemo(
        () => layoutCompareJson(cadeaubonLayouts) !== layoutCompareJson(originalCadeaubonLayoutsRef.current),
        [cadeaubonLayouts, savedTick]
    );

    // Verwerking (order processing). Only the confirmed-backend settings are
    // persisted; the rest stay in the UI as pending (see settingsToProcessing).
    const [processingConfig, setProcessingConfig] = useState(PROCESSING_CONFIG_DEFAULT);
    const originalProcessingConfigRef = useRef(PROCESSING_CONFIG_DEFAULT);
    // Raw CHEQUES_* discount JSON objects as loaded — kept so a save can rebuild
    // them over the originals without dropping codes the UI doesn't list.
    const originalProcChequeRef = useRef({ allowed: {}, asPayment: {}, addToReceipt: {} });
    // SHOW_HOME_IMAGE_GRID / SHOW_BRANDS_DISPLAY as loaded (config/configHelpers
    // HOME_BLOCK_FLAGS); undefined = the shop has no such row.
    const homeBlockFlagsRef = useRef({});
    // Raw PROCESS_TICKET_MODULE as loaded (LOOKBOOK|RESERVATION|RECEIPT). The
    // "no checkout" payment-location tile maps to LOOKBOOK; we keep the original
    // here so switching back to a checkout location preserves RESERVATION/RECEIPT.
    const originalTicketModuleRef = useRef('');
    const processingConfigDirty = useMemo(
        () => JSON.stringify(processingToSettings(processingConfig)) !== JSON.stringify(processingToSettings(originalProcessingConfigRef.current)),
        [processingConfig, savedTick]
    );

    // Custom customer fields (Verwerking → Customer fields) — CRUD list backed by
    // `customers_categories_groups`, separate from the settings config.
    const [customerCategoryGroups, setCustomerCategoryGroups] = useState([]);
    const originalCustomerGroupsRef = useRef([]);
    const customerGroupCompareJson = (arr) => JSON.stringify((arr || []).map(g => ({
        id: g.id ?? null,
        sft_id: g.codeGroup || '',
        nl: g.labels?.nl || '', en: g.labels?.en || '', fr: g.labels?.fr || '',
        reg: !!g.showOn?.registration, acc: !!g.showOn?.account, news: !!g.showOn?.newsletter,
    })));
    const customerGroupsDirty = useMemo(
        () => customerGroupCompareJson(customerCategoryGroups) !== customerGroupCompareJson(originalCustomerGroupsRef.current),
        [customerCategoryGroups, savedTick]
    );
    // POS + store/warehouse + salutation + country lists for the Verwerking dropdowns.
    const [posOptions, setPosOptions] = useState([]);
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [salutationOptions, setSalutationOptions] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);
    // Invalid POS values in Verwerking (same verdict the fields themselves show,
    // see processing/posCheck.js) — a dot on the sub-tab so they stand out
    // without opening the card.
    const posIssueCounts = useMemo(
        () => countPosIssues({ config: processingConfig, stores, posOptions }),
        [processingConfig, stores, posOptions]
    );
    // Which online payment providers have credentials set up (disables the rest).
    const [paymentProviderAvailability, setPaymentProviderAvailability] = useState({});
    // POS payment-type labels (kas_betaal1..9), kept raw with their kasvar.code
    // so the dropdown can be filtered to the receipt POS id (per-store labels).
    const [paymentTypeRows, setPaymentTypeRows] = useState([]); // [{n, value, code}]
    const receiptProcessPosId = processingConfig.receiptPos?.process;
    const paymentTypeOptions = useMemo(
        () => computePaymentTypeOptions(paymentTypeRows, receiptProcessPosId),
        [paymentTypeRows, receiptProcessPosId]
    );

    // Synchronisatie (product sync) — all controls map onto existing settings
    // keys (FETCH_*, MAP_*, PHOTO_*). No CRUD, no deploy. The store universe
    // (warehouseOptions) lets FETCH_FOR_STORES round-trip '00' ↔ all-selected.
    const [syncConfig, setSyncConfig] = useState(SYNC_CONFIG_DEFAULT);
    // POS article text types ([{id, name}] from www_textpreset via the
    // mini-api /admin/text-types proxy) — readable labels for the
    // description-texts card. Empty when the POS API is unreachable.
    const [textTypeOptions, setTextTypeOptions] = useState([]);

    // Merken-content (BrandContentCard) — content loads per picked brand
    // inside the card, but saving rides the GLOBAL Save button. The session
    // ref survives the card's unmount (tab switch) so the save can still
    // flush; the save ref is the card's own saver while mounted.
    const [brandContentDirty, setBrandContentDirty] = useState(false);
    const brandContentSessionRef = useRef(null); // { webshopId, brand, form, baseline } | null
    const brandContentSaveRef = useRef(null);
    const originalSyncConfigRef = useRef(SYNC_CONFIG_DEFAULT);
    const allStoreCodesForSync = useMemo(() => warehouseOptions.map(o => o.value), [warehouseOptions]);
    const syncConfigDirty = useMemo(
        () => JSON.stringify(syncToSettings(syncConfig, allStoreCodesForSync)) !== JSON.stringify(syncToSettings(originalSyncConfigRef.current, allStoreCodesForSync)),
        [syncConfig, allStoreCodesForSync, savedTick]
    );
    // Validate a guest customer id against the POS customer DB (auth backend,
    // sftData type "customer" → exact lookup on klant.klnr).
    // useCallback (like goToDataCountries): prop of the memoised ProcessingTab.
    const validateCustomerId = useCallback((id) => {
        const trimmed = String(id || '').trim();
        if (!trimmed) return Promise.resolve({ status: 'idle' });
        return SftGlobalData.post('sftData', { key: props.api, type: 'customer', code: trimmed })
            .then((r) => {
                const rows = (r?.data?.data && Array.isArray(r.data.data)) ? r.data.data : [];
                const row = rows.find(x => String(x.klnr).trim() === trimmed) || rows[0] || null;
                if (!row) return { status: 'invalid' };
                const person = [row.firstname, row.naam].map(s => String(s || '').trim()).filter(Boolean).join(' ');
                const name = [String(row.firmanaam || '').trim(), person].filter(Boolean).join(' — ');
                return { status: 'valid', name: name || trimmed };
            })
            .catch(() => ({ status: 'error' }));
    }, [props]);

    // Bulk-apply a country preset (allOff/allOn/default/belgium/benelux/europe).
    // This is a direct, destructive action on the shared countries table (resets
    // visible/prefered on ALL countries), NOT a saved setting — so it runs
    // immediately, not via Save. Reloads countryOptions afterwards. Returns bool.
    // useCallback: prop of the memoised ProcessingTab.
    const applyCountryPreset = useCallback(async (template) => {
        const webshopId = props.selectedWebshop?.id;
        if (!webshopId || !props.api) return false;
        try {
            const r = await SftGlobalData.post('webshop&action=proxy', {
                key: props.api, webshop_id: webshopId, sftUserId: props.login?.sft_user_id, user: props.login?.login,
                method: 'POST', path: '/countries/default-values', body: { template },
            });
            if (!r || !r.success || (r.data && r.data.status === false)) {
                if (props.notify) props.notify('Could not apply country preset', 4000, 'top-right', props.theme, 1);
                return false;
            }
            // Refresh the country dropdown from the now-mutated table.
            try {
                const cr = await SftGlobalData.post('webshop&action=proxy', {
                    key: props.api, webshop_id: webshopId, sftUserId: props.login?.sft_user_id, user: props.login?.login,
                    method: 'GET', path: '/countries',
                });
                if (cr?.success && Array.isArray(cr.data)) {
                    const adminLang = String(i18n.language || 'nl').slice(0, 2).toLowerCase();
                    const locKey = ['nl', 'en', 'fr'].includes(adminLang) ? `loc_${adminLang}` : 'loc_nl';
                    setCountryOptions(cr.data
                        .filter(x => x && x.iso != null && String(x.iso) !== '')
                        .map(x => ({ value: String(x.iso), label: String(x[locKey] || x.loc_nl || x.name || x.iso) })));
                }
            } catch { /* dropdown refresh is best-effort */ }
            if (props.notify) props.notify('Country preset applied', 3000, 'top-right', props.theme, 2);
            return true;
        } catch {
            if (props.notify) props.notify('Could not apply country preset', 4000, 'top-right', props.theme, 1);
            return false;
        }
    }, [props]);

    // Lazy-load a saved unlayer design for a dynamic page+language. The static
    // sidecars aren't reachable through the proxy (the mini-api router doesn't
    // serve /statics), so we use the dedicated `/dynamic-pages/{id}/design`
    // route which returns { design }. Returns the design object or null.
    // useCallback: prop of the memoised DynamicPageTab — fresh identity
    // would defeat that memo. Reads only props (stable across state renders).
    const fetchDynDesign = useCallback(async (serverId, lang) => {
        try {
            const r = await SftGlobalData.post('webshop&action=proxy', {
                key:        props.api,
                webshop_id: props.selectedWebshop?.id,
                sftUserId:  props.login?.sft_user_id,
                user:       props.login?.login,
                method:     'GET',
                path:       `/dynamic-pages/${serverId}/design?lang=${lang}`,
            });
            if (r && r.success && r.data) {
                const d = r.data.design;
                if (d && typeof d === 'object') return d;
                if (typeof d === 'string') { try { return JSON.parse(d); } catch { return null; } }
            }
        } catch { /* ignore — editor falls back to blank */ }
        return null;
    }, [props]);
    // The stored body of a dynamic page / product container, per language, as
    // the shop serves it (file or column) — the config bundle leaves body_* out.
    // Returns { nl, en, fr } or null when it could not be fetched (then the text
    // editors stay closed rather than open empty).
    const fetchContentBody = useCallback(async (resource, serverId) => {
        try {
            const r = await SftGlobalData.post('webshop&action=proxy', {
                key:        props.api,
                webshop_id: props.selectedWebshop?.id,
                sftUserId:  props.login?.sft_user_id,
                user:       props.login?.login,
                method:     'GET',
                path:       `/${resource}/${serverId}/body`,
            });
            const b = r && r.success && r.data && r.data.body;
            if (b && typeof b === 'object') {
                return { nl: String(b.nl || ''), en: String(b.en || ''), fr: String(b.fr || '') };
            }
        } catch { /* fall through */ }
        return null;
    }, [props]);
    const fetchDynBody = useCallback((serverId) => fetchContentBody('dynamic-pages', serverId), [fetchContentBody]);
    // Store address → { status, lat, lng, error_message } through the shop
    // (mini-api GET /admin/geocode, which uses the shop's own Maps key like
    // the legacy admin). null when the shop cannot answer (older mini-api) —
    // the About page then tries the lookup from the browser.
    const geocodeViaShop = useCallback(async (address) => {
        try {
            const r = await SftGlobalData.post('webshop&action=proxy', {
                key:        props.api,
                webshop_id: props.selectedWebshop?.id,
                sftUserId:  props.login?.sft_user_id,
                user:       props.login?.login,
                method:     'GET',
                path:       `/admin/geocode?address=${encodeURIComponent(address)}`,
            });
            if (r && r.success && r.data && typeof r.data.status === 'string') return r.data;
        } catch { /* fall through */ }
        return null;
    }, [props]);
    const [activeId, setActiveId] = useState(null);
    const [configBlock, setConfigBlock] = useState(null);
    const [containerModalOpen, setContainerModalOpen] = useState(false);
    const [containerData, setContainerData] = useState(DEFAULT_CONTAINER);
    const [editingContainerId, setEditingContainerId] = useState(null);
    // set by saveContainer: persist right after the dialog's own save (see the
    // effect after saveConfig)
    const [saveAfterContainer, setSaveAfterContainer] = useState(false);
    const [editorLang, setEditorLang] = useState(0); // 0=NL, 1=EN, 2=FR
    const [editorFullscreen, setEditorFullscreen] = useState(false);
    // The container's stored body (text per language) for the dialog:
    // 'ok' = loaded (or nothing to load), 'loading', 'error' = could not be
    // fetched → the text editors stay closed and the body is not saved.
    const [containerBodyStatus, setContainerBodyStatus] = useState('ok');
    const [containerModeBusy, setContainerModeBusy] = useState(false);
    const [editorInlineSlot, setEditorInlineSlot] = useState(null);
    const [slotRect, setSlotRect] = useState(null);
    const editorRefNL = useRef(null);
    const editorRefEN = useRef(null);
    const editorRefFR = useRef(null);
    const dialogContentRef = useRef(null);
    const savedScrollRef = useRef(0);

    // Front-page composition data fetched from the webshop's mini-api. We
    // load the full /admin/settings dump on webshop change and use:
    //   - `productContainers`     → mapped to `pd_cnt-<id>` blocks
    //   - `HOME_SECTIONS_ORDER`   → comma-separated list of active block ids
    // to populate the Active/Available columns. Container create/config/delete
    // saves via saveContainer/deleteContainer + HOME_SECTIONS_ORDER on Save.
    const [configLoading, setConfigLoading] = useState(false);
    const [configSaving, setConfigSaving] = useState(false);
    // Result of the last Save that could not save everything (see
    // config/saveSections.js). Drives the report dialog and, once that is
    // closed, the reminder bar under the tabs. null = last save was clean.
    const [saveReport, setSaveReportState] = useState(null);
    const [saveReportOpen, setSaveReportOpen] = useState(false);
    const setSaveReport = useCallback((report) => {
        setSaveReportState(report || null);
        setSaveReportOpen(!!report);
    }, []);
    // A report belongs to the shop it was made on.
    useEffect(() => { setSaveReport(null); }, [props.selectedWebshop?.id, setSaveReport]);
    // Scroll-aware save: show a floating save button when the header save button
    // scrolls out of view on long pages, and hide it again once it's back in
    // view. This null-root IntersectionObserver is the version confirmed working
    // in Chrome. (Firefox handling is being worked on separately.) A callback
    // ref keeps the observer attached across the screen's conditional renders.
    const [headerSaveVisible, setHeaderSaveVisible] = useState(true);
    const saveObserverRef = useRef(null);
    const headerSaveRef = useCallback((node) => {
        if (saveObserverRef.current) { saveObserverRef.current.disconnect(); saveObserverRef.current = null; }
        if (!node) { setHeaderSaveVisible(true); return; }
        if (typeof IntersectionObserver === 'undefined') return;
        saveObserverRef.current = new IntersectionObserver(
            ([entry]) => setHeaderSaveVisible(entry.isIntersecting),
            { threshold: 0 },
        );
        saveObserverRef.current.observe(node);
    }, []);
    const [configError, setConfigError] = useState(null);
    // Container config/create/delete edits: a simple dirty flag (set by
    // saveContainer/deleteContainer, cleared after save). `originalContainerIdsRef`
    // holds the `pd_cnt-<id>` block ids that exist server-side, for delete-diffing;
    // seeded at load, refreshed after each save.
    const [containersDirty, setContainersDirty] = useState(false);
    const originalContainerIdsRef = useRef(new Set());
    // Stores the saved HOME_SECTIONS_ORDER value as it sits on the server.
    // We diff this against the live `active` list to know when the order is
    // dirty and what to send in the PATCH.
    const [homeSectionsOrder, setHomeSectionsOrder] = useState('');
    // Landing page on/off (SHOW_LANDING -- the legacy "Activeer landingspagina").
    // Off = the storefront's home route redirects to the explorer. `saved...` is
    // the value on the server: the dirty flag and the PATCH on Save diff against it.
    const [showLanding, setShowLanding] = useState(true);
    const [savedShowLanding, setSavedShowLanding] = useState(true);
    // Same for the 404 page — its own active order (PAGE_404_SECTIONS_ORDER,
    // default 'text'), diffed against the live `active404` list.
    const [page404SectionsOrder, setPage404SectionsOrder] = useState('');
    // Per-shop labels keyed by legacy backend field name. Source of truth
    // is the customer's `kasvars` table reached via the central Auth
    // backend's `sftData` action. Known mappings:
    //   category_1..7 ↔ Code1Oms..Code7Oms
    //   detail_1..5   ↔ Levart1Oms..Levart5Oms
    // Populated by the parallel fetch in the load effect; cards fall back
    // to their cfg_pdi_details_* i18n keys when a slot has no value here.
    const [shopFieldLabels, setShopFieldLabels] = useState({});

    const toggleEditorFullscreen = () => {
        if (!editorFullscreen && dialogContentRef.current) {
            savedScrollRef.current = dialogContentRef.current.scrollTop;
        }
        setEditorFullscreen((v) => !v);
    };

    // Restore scroll position after exiting fullscreen
    useEffect(() => {
        if (!editorFullscreen && dialogContentRef.current && savedScrollRef.current) {
            // Wait for layout to settle after portal content returns
            requestAnimationFrame(() => {
                if (dialogContentRef.current) {
                    dialogContentRef.current.scrollTop = savedScrollRef.current;
                }
            });
        }
    }, [editorFullscreen]);

    // Attempt to call unlayer.init() as early as possible so features config
    // is registered before createEditor runs inside the wrapper.
    useEffect(() => {
        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 200; // ~10s
        const tryInit = () => {
            if (cancelled) return;
            if (window.unlayer && typeof window.unlayer.init === 'function') {
                try {
                    window.unlayer.init({
                        features: {
                            preview: false, // DIAGNOSTIC
                            textEditor: {
                                cleanPaste: false,
                                customButtons: [
                                    {
                                        name: 'my_button', // Unique identifier for the button
                                        text: 'My Button', // The label that will appear on the button
                                        icon: 'bookmark', // Icon that will appear on the button (optional)
                                        onSetup: () => {}, // Function executed when the button is set up
                                        onAction: (data, callback) => {
                                            callback(data.text + ' Updated'); // Perform action and update the text
                                        },
                                    },
                                ],
                            },
                        },
                    });
                } catch (e) { /* ignore */ }
                return;
            }
            if (++attempts < maxAttempts) {
                setTimeout(tryInit, 50);
            }
        };
        tryInit();
        return () => { cancelled = true; };
    }, []);

    // ── Load front-page composition from the webshop's mini-api ─────────────
    // Fetches `/admin/settings`, extracts product containers and
    // HOME_SECTIONS_ORDER, and partitions the block catalog into
    // Active (in saved order) + Available (everything else). Re-runs on
    // webshop switch.
    useEffect(() => loadWebshopConfig({
        DEFAULT_EXTRA_FOOTER_CONFIG,
        DEFAULT_FOOTER_CONFIG,
        DEFAULT_GLOBAL_WIDTHS,
        DEFAULT_HEADER_LOGO_CONFIG,
        DEFAULT_IMAGE_GALLERY_CONFIG,
        DEFAULT_NAVIGATION_CONFIG,
        DEFAULT_PRODUCT_DETAIL_INFO_CONFIG,
        carouselImgBaseRef,
        gridImgBaseRef,
        layoutImgBaseRef,
        looksCtaIdRef,
        navbarOrderLevelRef,
        originalBannersRef,
        originalBrandsConfigRef,
        originalBrandsRawJsonRef,
        originalCadeaubonConfigRef,
        originalCadeaubonLayoutsRef,
        originalCardsRef,
        originalCarouselConfigRef,
        originalContainerIdsRef,
        originalCustomerGroupsRef,
        originalDynamicPagesRef,
        originalExtraFooterConfigRef,
        originalFooterConfigRef,
        originalGlobalWidthsRef,
        originalGroupsFilterConfigRef,
        originalHeaderLogoConfigRef,
        originalHoursSeparatorRef,
        originalImageGalleryConfigRef,
        originalImageGridConfigRef,
        originalLooksConfigRef,
        originalMapSettingsRef,
        originalNavbarOrderRef,
        originalNavigationConfigRef,
        originalOverigeConfigRef,
        originalPlaceholdersRef,
        originalProcChequeRef,
        homeBlockFlagsRef,
        originalProcessingConfigRef,
        originalProductCardConfigRef,
        originalProductDetailInfoConfigRef,
        originalProductGridConfigRef,
        originalStaticPagesRef,
        originalStoresRef,
        originalStyleSettingsRef,
        originalSyncConfigRef,
        originalTicketModuleRef,
        productHoverMaxRowsRef,
        cartLayout,
        setCartLayout,
        originalCartLayoutRef,
        props,
        setActive,
        setActive404,
        setAvailable,
        setAvailable404,
        setBanners,
        setBrandsConfig,
        setCadeaubonConfig,
        setCadeaubonLayouts,
        setCadeaubonPlaceholders,
        setCards,
        setCarouselConfig,
        setConfigError,
        setConfigLoading,
        setContainersDirty,
        setCountryOptions,
        setCustomerCategoryGroups,
        setDynamicPages,
        setExtraFooterConfig,
        setFooterConfig,
        setGlobalWidths,
        setGmapsApiKey,
        setGroupsFilterConfig,
        setHeaderLogoConfig,
        setHomeSectionsOrder,
        setShowLanding,
        setSavedShowLanding,
        setHoursSeparator,
        setImageGalleryConfig,
        setImageGridConfig,
        setLooksConfig,
        setMapSettings,
        setNavFilterStyle,
        setNavbarOrder,
        setNavigationConfig,
        setOverigeConfig,
        setPage404SectionsOrder,
        setPaymentProviderAvailability,
        setPaymentTypeRows,
        setPosOptions,
        setProcessingConfig,
        setProductCardConfig,
        setProductDetailInfoConfig,
        setProductGridConfig,
        setSalutationOptions,
        setShopFieldLabels,
        setStaticPages,
        setStores,
        setStyleSettings,
        setSyncConfig,
        setTextTypeOptions,
        setWarehouseOptions,
    }), [props.api, props.login?.sft_user_id, props.login?.login, props.selectedWebshop?.id]);

    // Build the order string from the current `active` list. IDs were
    // populated with canonical casing (`carousel`, `imageGrid`, `pd_cnt-<id>`)
    // when we built the catalog, so a plain join produces the storefront's
    // expected HOME_SECTIONS_ORDER format.
    const desiredHomeSectionsOrder = active.map(b => b.id).join(',');
    const orderDirty = desiredHomeSectionsOrder !== homeSectionsOrder;
    const showLandingDirty = showLanding !== savedShowLanding;
    const desiredPage404Order = active404.map(b => b.id).join(',');
    const order404Dirty = desiredPage404Order !== page404SectionsOrder;

    // After a successful save we want the storefront's static caches
    // regenerated so visitors see the new values without a full sync. Mirrors
    // the legacy admin's behaviour and the iframe-fire pattern used in the
    // Actions tab. Best-effort: if the legacy script returns an error we
    // surface it but the save itself still counts as successful.
    // `detail` (e.g. '__generateCarousel') runs that ONE generator with
    // force=true — matches the legacy admin, which fires
    // `?detail=__generateCarousel` after a carousel image upload. Without it,
    // the plain run calls __generateCarousel(false), which only rewrites
    // carousel.json when the row changed in the last 11 min — and an image
    // upload's UPDATE doesn't bump `updated_at`, so the new image wouldn't show
    // until a forced regen. `silent` suppresses the toast for piggy-backed runs.
    const fireGenerateStatics = (detail = null, { silent = false } = {}) => {
        const base = shopImageBase(props.selectedWebshop?.display_name);
        if (!base) return;
        // The cron lives under server/v1/crons/, NOT at the domain root —
        // `base` has the `/server/v1/mini-api` suffix stripped, so we re-add
        // `/server/v1`. Matches the legacy admin's exact request:
        // {base}/server/v1/crons/generate_statics.php?lng=nl&__cb_=...
        const query   = detail ? `detail=${encodeURIComponent(detail)}` : 'lng=nl';
        const fullUrl = `${base}/server/v1/crons/generate_statics.php?${query}&__cb_=${Date.now()}`;
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        let done = false;
        const finish = (ok, msg) => {
            if (done) return;
            done = true;
            if (!silent && props.notify) props.notify(msg, ok ? 4000 : 6000, 'top-right', props.theme, ok ? 2 : 1);
            setTimeout(() => iframe.remove(), 100);
        };
        iframe.onload  = () => finish(true,  'Statics regenerated');
        iframe.onerror = () => finish(false, 'Statics regeneration failed');
        // Most storefront caches finish well under 60s; give it up to that
        // before reporting a timeout so the user knows to retry manually.
        setTimeout(() => finish(false, 'Statics regeneration timed out'), 60000);
        iframe.src = fullUrl;
        document.body.appendChild(iframe);
    };

    const saveConfig = async () => runSaveConfig({
        STATIC_PAGE_DEFAULT,
        allStoreCodesForSync,
        banners,
        bannersDirty,
        blankColumn,
        brandContentDirty,
        brandContentSaveRef,
        brandContentSessionRef,
        setBrandContentDirty,
        brandsConfig,
        brandsConfigDirty,
        cadeaubonConfig,
        cadeaubonConfigDirty,
        cadeaubonLayouts,
        cadeaubonLayoutsDirty,
        cadeaubonPlaceholders,
        cadeaubonPlaceholdersDirty,
        cards,
        cardsDirty,
        carouselConfig,
        carouselDirty,
        carouselImgBaseRef,
        containerBlocks,
        containersDirty,
        customerCategoryGroups,
        customerGroupsDirty,
        desiredHomeSectionsOrder,
        showLanding,
        desiredPage404Order,
        dynamicPages,
        dynamicPagesDirty,
        extraFooterConfig,
        extraFooterDirty,
        fireGenerateStatics,
        footerConfig,
        footerDirty,
        globalWidths,
        globalWidthsDirty,
        gridImgBaseRef,
        groupsFilterConfig,
        groupsFilterDirty,
        headerLogoConfig,
        headerLogoDirty,
        hoursSeparator,
        imageGalleryConfig,
        imageGalleryDirty,
        imageGridConfig,
        imageGridDirty,
        layoutImgBaseRef,
        looksConfig,
        looksConfigDirty,
        looksCtaIdRef,
        mapSettings,
        mapSettingsDirty,
        navbarOrder,
        navbarOrderDirty,
        navigationConfig,
        navigationDirty,
        order404Dirty,
        orderDirty,
        showLandingDirty,
        originalBannersRef,
        originalBrandsConfigRef,
        originalBrandsRawJsonRef,
        originalCadeaubonConfigRef,
        originalCadeaubonLayoutsRef,
        originalCardsRef,
        originalCarouselConfigRef,
        originalContainerIdsRef,
        originalCustomerGroupsRef,
        originalDynamicPagesRef,
        originalExtraFooterConfigRef,
        originalFooterConfigRef,
        originalGlobalWidthsRef,
        originalGroupsFilterConfigRef,
        originalHeaderLogoConfigRef,
        originalHoursSeparatorRef,
        originalImageGalleryConfigRef,
        originalImageGridConfigRef,
        originalLooksConfigRef,
        originalMapSettingsRef,
        originalNavbarOrderRef,
        originalNavigationConfigRef,
        originalOverigeConfigRef,
        originalPlaceholdersRef,
        originalProcChequeRef,
        homeBlockFlagsRef,
        originalProcessingConfigRef,
        originalProductCardConfigRef,
        originalProductDetailInfoConfigRef,
        originalProductGridConfigRef,
        originalStaticPagesRef,
        originalStoresRef,
        originalStyleSettingsRef,
        originalSyncConfigRef,
        originalTicketModuleRef,
        overigeConfig,
        overigeConfigDirty,
        processingConfig,
        processingConfigDirty,
        productCardConfig,
        productCardDirty,
        productDetailInfoConfig,
        productDetailInfoDirty,
        productGridConfig,
        productGridDirty,
        productHoverMaxRowsRef,
        cartLayout,
        setCartLayout,
        originalCartLayoutRef,
        props,
        separatorDirty,
        setActive,
        setActive404,
        setAvailable,
        setAvailable404,
        setBanners,
        setCadeaubonLayouts,
        setCadeaubonPlaceholders,
        setCarouselConfig,
        setConfigSaving,
        setContainersDirty,
        setCustomerCategoryGroups,
        setDynamicPages,
        setFooterConfig,
        setHeaderLogoConfig,
        setHomeSectionsOrder,
        setSavedShowLanding,
        setImageGridConfig,
        setPage404SectionsOrder,
        setSavedTick,
        setSaveReport,
        staticPages,
        setStaticPages,
        setExtraFooterConfig,
        staticPagesDirty,
        stores,
        storesDirty,
        styleSettings,
        styleSettingsDirty,
        syncConfig,
        syncConfigDirty,
        validateCustomerId,
    });

    // "Wijzigingen opslaan" in the product-container dialog persists straight
    // away, like the Save in the other block dialogs (it used to only stage the
    // change until the global Save, which read as "not saved"). saveContainer
    // only sets state, so the save runs one render later -- with the updated
    // block lists and containersDirty -- and waits for a save already running.
    useEffect(() => {
        if (!saveAfterContainer || configSaving) return;
        setSaveAfterContainer(false);
        saveConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveAfterContainer, configSaving]);

    // Track inline slot's position so the editor overlay can follow it.
    // The editor lives in a single portal target (document.body) so its iframe
    // never remounts; we just reposition with CSS. We also track the DialogContent
    // rect so we can clip the overlay to the dialog's visible area (prevents the
    // editor from overlapping modal content when the user scrolls).
    useLayoutEffect(() => {
        if (!containerModalOpen || !editorInlineSlot || editorFullscreen) return;

        const updateRect = () => {
            if (!editorInlineSlot) return;
            const rect = editorInlineSlot.getBoundingClientRect();
            const dialogRect = dialogContentRef.current?.getBoundingClientRect();
            setSlotRect({
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                dialogTop: dialogRect?.top ?? 0,
                dialogBottom: dialogRect?.bottom ?? window.innerHeight,
            });
        };

        updateRect();

        const ro = new ResizeObserver(updateRect);
        ro.observe(editorInlineSlot);

        window.addEventListener('resize', updateRect);
        // capture=true to catch scroll events from DialogContent
        window.addEventListener('scroll', updateRect, true);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', updateRect);
            window.removeEventListener('scroll', updateRect, true);
        };
    }, [containerModalOpen, editorInlineSlot, editorFullscreen, editorLang]);

    // Style tab: new layout (mockup + gear-modals)
    const [styleSettings, setStyleSettings] = useState({
        global: {
            background: '#ffffff',
            text: '#111111',
            border: '#e4e7eb',
            primary: '#1976d2',
            font: 'Roboto',
        },
        visibility: {
            languageBar: true,
            footer: true,
            extra: true,
            backToTopPosition: 'right',
            // "Recently viewed" band: explorer/detail/cart (SHOW_RECENTLY) and home (SHOW_RECENTLY_ON_HOME)
            recently:       true,
            recentlyOnHome: true,
            // Languages: visibleLanguages = active on storefront (VISIBILE_LANGUAGES),
            // adminLanguages = editable in the admin (ADMIN_LANGUAGES, a superset).
            visibleLanguages: ['NL', 'EN', 'FR'],
            adminLanguages:   ['NL', 'EN', 'FR'],
            // LANGUAGE_DEFAULT ('browser' or a language) and INTERNAL_LANGUAGE
            // (POS tickets, internal mails), both lower case like the storefront.
            defaultLanguage:  'browser',
            internalLanguage: 'nl',
        },
        sections: {
            language:    { bgColor: '#f5f5f5', textColor: '#111111', activeColor: '#dcdcdc', position: 'right', enabledLanguages: ['NL', 'EN', 'FR'] },
            header:      { bgColor: '#000000', textColor: '#ffffff' },
            menu:        { bgColor: '#000000', textColor: '#ffffff', accentColor: '#e53935', accentTextColor: '#ffffff', salesBgColor: '#e57373', salesTextColor: '#f5f5f5', alignment: 'center', showCartBadge: true, useCustomHover: false, primaryHover: '#1565c0', primaryHoverText: '#ffffff', secondaryHover: '#333333', secondaryHoverText: '#ffffff' },
            image:       { width: 50, shownData: 'productImage' },
            title:       { textColor: '#111111', fontSize: 18, position: 'left' },
            subtitle:    { textColor: '#666666', fontSize: 14 },
            text:        { textColor: '#333333', fontSize: 13 },
            price:       { mainColor: '#111111', discountColor: '#e53935', discountTextColor: '#ffffff' },
            addToCart:   { bgColor: '#1976d2', textColor: '#ffffff' },
            sideFilter:  { borderColor: '#e4e7eb', mobileActiveColor: '', mobileActiveParent: false },
            recent:      { bgColor: '#f5f5f5', textColor: '#262626' },
            footer:      { bgColor: '#f5f5f5', textColor: '#111111', columns: 3, showPaymentMethods: false },
            footerExtra: { bgColor: '#ffffff', textColor: '#666666', columns: 3, paymentBgColor: '#ffffff' },
        },
    });
    // Dirty check for style settings — compares every legacy-mapped colour
    // path against the snapshot we took at load time.
    const styleSettingsDirty = useMemo(() => {
        const orig = originalStyleSettingsRef.current;
        if (!orig) return false;
        if (STYLE_COLOR_PATHS.some(p => readByPath(styleSettings, p.path) !== readByPath(orig, p.path))) return true;
        // Non-colour fields in the Global / Visibility panel.
        if ((styleSettings.global?.font) !== (orig.global?.font)) return true;
        // Custom-hover toggle (the 4 hover colours ride STYLE_COLOR_PATHS above).
        if ((styleSettings.sections?.menu?.useCustomHover) !== (orig.sections?.menu?.useCustomHover)) return true;
        const v = styleSettings.visibility || {}, ov = orig.visibility || {};
        if (v.languageBar !== ov.languageBar) return true;
        if (v.footer !== ov.footer) return true;
        if (v.backToTopPosition !== ov.backToTopPosition) return true;
        if (!!v.recently !== !!ov.recently || !!v.recentlyOnHome !== !!ov.recentlyOnHome) return true;
        if (v.defaultLanguage !== ov.defaultLanguage || v.internalLanguage !== ov.internalLanguage) return true;
        if (!!styleSettings.sections?.sideFilter?.mobileActiveParent !== !!orig.sections?.sideFilter?.mobileActiveParent) return true;
        if (toLangCsv(v.visibleLanguages) !== toLangCsv(ov.visibleLanguages)) return true;
        if (toLangCsv(v.adminLanguages) !== toLangCsv(ov.adminLanguages)) return true;
        return false;
    }, [styleSettings, savedTick]);
    // { section: 'language'|'title'|..., mode: 'color'|'config', anchorPos? } or null
    const [activeStyleSection, setActiveStyleSection] = useState(null);
    const openStyleModal = (section, mode, anchorPos = null) => setActiveStyleSection({ section, mode, anchorPos });
    // Timestamp of the last color-picker interaction; used to suppress the Popover
    // from closing while the native OS color dialog is open (it steals focus and
    // MUI interprets the return click as a backdrop click).
    const colorPickerInteractionRef = useRef(0);

    // Google Fonts catalog (fetched from backend, cached in localStorage for 7 days)
    // Cache stores only Google's API data; DEFAULT_FONTS is prepended at read time.
    const [googleFonts, setGoogleFonts] = useState([]);
    useEffect(() => {
        const CACHE_KEY = 'sft_google_fonts_v1';
        const TTL_MS = 7 * 24 * 60 * 60 * 1000;
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed?.fetchedAt && Date.now() - parsed.fetchedAt < TTL_MS && Array.isArray(parsed.fonts)) {
                    // Strip any stale DEFAULT_FONTS from old cache entries before prepending fresh ones
                    const pureGoogle = parsed.fonts.filter(f => f.category !== 'default');
                    setGoogleFonts([...DEFAULT_FONTS, ...pureGoogle]);
                    return;
                }
            }
        } catch (e) { /* ignore cache errors */ }

        const url = SftGlobalData.baseURL_API + 'google&action=fetchFonts';
        axios.post(url, { key: props.api }, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        }).then((resp) => {
            const fonts = resp?.data?.data;
            if (Array.isArray(fonts) && fonts.length > 0) {
                // Stable sort by category (preserves popularity order within each group)
                const sorted = fonts
                    .map((f, i) => ({ ...f, _i: i }))
                    .sort((a, b) => {
                        const c = (a.category || '').localeCompare(b.category || '');
                        return c !== 0 ? c : a._i - b._i;
                    })
                    .map(({ _i, ...rest }) => rest);
                setGoogleFonts([...DEFAULT_FONTS, ...sorted]);
                try {
                    localStorage.setItem(CACHE_KEY, JSON.stringify({ fetchedAt: Date.now(), fonts: sorted }));
                } catch (e) { /* quota/serialization — skip */ }
            }
        }).catch(() => { /* silent — the fallback options still render */ });
    }, []);

    // Inject a <link> for the currently selected global font so the mockup
    // preview actually renders in that typeface. Each unique font is injected once.
    // System fonts (CSS stacks like "Arial, Helvetica, sans-serif") are skipped —
    // the browser resolves those locally and Google Fonts doesn't host them.
    const loadedFontLinksRef = useRef(new Set());
    useEffect(() => {
        const family = styleSettings?.global?.font;
        if (!family) return;
        if (loadedFontLinksRef.current.has(family)) return;
        // CSS font-family stacks contain commas — they're system fonts, not Google fonts.
        if (family.indexOf(',') !== -1) {
            loadedFontLinksRef.current.add(family);
            return;
        }
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}&display=swap`;
        document.head.appendChild(link);
        loadedFontLinksRef.current.add(family);
    }, [styleSettings?.global?.font]);

    const updateStyleGlobal = (field, value) => {
        setStyleSettings(prev => ({ ...prev, global: { ...prev.global, [field]: value } }));
    };
    const updateStyleVisibility = (field, value) => {
        setStyleSettings(prev => ({ ...prev, visibility: { ...prev.visibility, [field]: value } }));
    };
    const updateStyleSection = (section, field, value) => {
        setStyleSettings(prev => ({
            ...prev,
            sections: { ...prev.sections, [section]: { ...prev.sections[section], [field]: value } },
        }));
    };

    useEffect(() => {
        if (props.changeScreen) props.changeScreen('webshop-config');
    }, []);

    // startTransition keeps the current tab visible while a lazy sub-tab's chunk
    // loads, instead of flashing the Suspense skeleton (which caused a visible
    // flicker + layout jump on fast/cached loads).
    const handleTabChange = (_e, newValue) => startTransition(() => setTabValue(newValue));

    // Header navigation search (jump to any tab/card/modal).
    const openBlockConfig = (blockId) => {
        const blk = STATIC_BLOCKS.find(b => b.id === blockId);
        if (blk) setConfigBlock(blk);
    };
    // Adapters only — the search state itself lives inside WebshopSearchBar
    // (F01), so typing there never re-renders this screen. theme/notify were
    // accepted but never read by the hook; dropped.
    const search = useWebshopSearch({
        t:        props.t,
        can, // role gating: the search bar hides results the role can't reach
        setTabValue, setFeaturesTab, setPagesTab, setStyleTab, setProcTab, setSyncTab,
        openStyleModal, openBlockConfig,
    });

    // Helpers to find which list/index an item lives in
    // 'active' resolves to whichever page's active list is currently visible.
    // The Front page tab uses `active`; the 404 page tab uses `active404`.
    // The 404 page is the second sub-tab under the Pages main tab
    // (tabValue 0 = Pages, pagesTab 1 = 404) — NOT a top-level tab. The old
    // `tabValue === 2` check pointed at the Features tab, so drags on the 404
    // page were silently mutating the Front page's active list.
    const isPage404Tab = () => tabValue === 0 && pagesTab === 1;
    const getActiveList = () => (isPage404Tab() ? active404 : active);
    const setActiveList = (updater) => {
        if (isPage404Tab()) setActive404(updater);
        else setActive(updater);
    };
    // Available list is per-page too (Front page vs 404), so a drag on one
    // tab never mutates the other tab's catalog.
    const getAvailableList = () => (isPage404Tab() ? available404 : available);
    const setAvailableList = (updater) => {
        if (isPage404Tab()) setAvailable404(updater);
        else setAvailable(updater);
    };

    const findContainer = (id) => {
        if (getAvailableList().some(i => i.id === id)) return 'available';
        if (getActiveList().some(i => i.id === id)) return 'active';
        if (id === 'available' || id === 'active') return id;
        return null;
    };

    const getList = (column) => (column === 'active' ? getActiveList() : getAvailableList());
    const setList = (column, updater) => {
        if (column === 'active') setActiveList(updater);
        else setAvailableList(updater);
    };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const handleDragStart = (event) => setActiveId(event.active.id);

    const handleDragOver = (event) => {
        const { active: activeDrag, over } = event;
        if (!over) return;

        const activeContainer = findContainer(activeDrag.id);
        const overContainer = findContainer(over.id);
        if (!activeContainer || !overContainer) return;
        if (activeContainer === overContainer) return;

        // Cross-column: move item between lists during drag for live feedback
        const activeItems = getList(activeContainer);
        const overItems = getList(overContainer);
        const activeIndex = activeItems.findIndex(i => i.id === activeDrag.id);
        const overIndex = overItems.findIndex(i => i.id === over.id);
        if (activeIndex < 0) return;

        const movedItem = activeItems[activeIndex];
        setList(activeContainer, prev => prev.filter(i => i.id !== activeDrag.id));
        setList(overContainer, prev => {
            const insertAt = overIndex >= 0 ? overIndex : prev.length;
            const withNew = [...prev];
            withNew.splice(insertAt, 0, movedItem);
            return withNew;
        });
    };

    const handleDragEnd = (event) => {
        const { active: activeDrag, over } = event;
        setActiveId(null);
        if (!over) return;

        const activeContainer = findContainer(activeDrag.id);
        const overContainer = findContainer(over.id);
        if (!activeContainer || !overContainer) return;

        if (activeContainer === overContainer) {
            const list = getList(activeContainer);
            const fromIndex = list.findIndex(i => i.id === activeDrag.id);
            const toIndex = list.findIndex(i => i.id === over.id);
            if (fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
                setList(activeContainer, arrayMove(list, fromIndex, toIndex));
            }
        }
    };

    const handleDragCancel = () => setActiveId(null);

    const openNewContainerModal = () => {
        setEditingContainerId(null);
        setContainerData(DEFAULT_CONTAINER);
        setEditorLang(0);
        setEditorFullscreen(false);
        setContainerBodyStatus('ok');
        setContainerModalOpen(true);
    };

    // The shop's row id of a container block (null while it only exists here).
    const containerDbId = (item) => {
        if (item?.container?.id != null) return item.container.id;
        const m = /^pd_cnt-(\d+)$/.exec(String(item?.id || ''));
        return m ? Number(m[1]) : null;
    };
    const designRowCount = (d) => (d && d.body && Array.isArray(d.body.rows) ? d.body.rows.length : 0);

    // Fetch the stored text of the container being edited into text{NL,EN,FR}.
    // Only fills a language the dialog has no text for yet, so reopening a
    // container edited earlier in this session keeps those edits.
    const loadContainerBody = (dbId) => {
        setContainerBodyStatus('loading');
        return fetchContentBody('product-containers', dbId).then((body) => {
            if (!body) { setContainerBodyStatus('error'); return null; }
            setContainerData(prev => ({
                ...prev,
                textNL: prev.textNL !== undefined ? prev.textNL : body.nl,
                textEN: prev.textEN !== undefined ? prev.textEN : body.en,
                textFR: prev.textFR !== undefined ? prev.textFR : body.fr,
                bodyLoaded: true,
            }));
            setContainerBodyStatus('ok');
            return body;
        });
    };

    const openEditContainerModal = (item) => {
        setEditingContainerId(item.id);
        setContainerData(item.config || DEFAULT_CONTAINER);
        setEditorLang(0);
        setEditorFullscreen(false);
        setContainerModalOpen(true);
        const dbId = containerDbId(item);
        // Stored text (plain text / own HTML, or the html the designer last
        // wrote): needed to edit a text container and to switch forms.
        if (dbId == null || (item.config && item.config.bodyLoaded)) setContainerBodyStatus('ok');
        else loadContainerBody(dbId);
        // Load the saved per-language body designs so an existing container's
        // content is editable (not blank). Each design is stashed in state — so a
        // not-yet-ready editor picks it up via onEditorReady AND the save flow can
        // capture it — and pushed straight into an already-ready editor.
        if (dbId == null) return;
        ['nl', 'en', 'fr'].forEach((l) => {
            SftGlobalData.post('webshop&action=proxy', {
                key:        props.api,
                webshop_id: props.selectedWebshop?.id,
                sftUserId:  props.login?.sft_user_id,
                user:       props.login?.login,
                method:     'GET',
                path:       `/product-containers/${dbId}/design?lang=${l}`,
            }).then((r) => {
                const design = r?.data?.design ?? null;
                if (!design) return;
                const L = l.toUpperCase();
                updateContainerField(`design${L}`, design);
                // remember that the shop holds a design for this language, so
                // emptying it in the designer is saved too
                if (designRowCount(design) > 0) updateContainerField(`hadDesign${L}`, true);
                const ref = getEditorRef(L);
                if (ref?.current?.editor) {
                    try { ref.current.editor.loadDesign(design); } catch (e) { /* ignore */ }
                }
            }).catch(() => { /* editor just opens without a preloaded design */ });
        });
    };

    const closeContainerModal = () => {
        setContainerModalOpen(false);
        setEditingContainerId(null);
        setEditorFullscreen(false);
    };

    const captureEditorDesigns = () => {
        return new Promise((resolve) => {
            // Capture BOTH the unlayer design JSON and the rendered HTML per
            // language: the design lets us re-open the editor, the html is what
            // the storefront actually serves (via the file/iframe render path).
            const out = {};
            const refs = [
                { lang: 'NL', ref: editorRefNL },
                { lang: 'EN', ref: editorRefEN },
                { lang: 'FR', ref: editorRefFR },
            ];
            let completed = 0;
            const done = () => { completed++; if (completed === refs.length) resolve(out); };
            refs.forEach(({ lang, ref }) => {
                if (ref.current?.editor) {
                    ref.current.editor.exportHtml((data) => {
                        out[`design${lang}`] = data?.design ?? null;
                        out[`html${lang}`]   = data?.html ?? '';
                        done();
                    });
                } else {
                    done();
                }
            });
            // Timeout safety in case editor refs aren't ready
            setTimeout(() => resolve(out), 1500);
        });
    };

    // Switch the storage form of the container's body. Leaving the designer
    // takes its content along as text: the exported html of each language that
    // holds a design, the stored text for the others.
    const switchContainerEditor = async (next) => {
        const cur = containerData.editor || 'unlayer';
        if (next === cur || containerBodyStatus !== 'ok') return;
        if (cur !== 'unlayer') {
            setContainerData(prev => ({ ...prev, editor: next }));
            return;
        }
        setContainerModeBusy(true);
        const captured = await captureEditorDesigns();
        setContainerModeBusy(false);
        setEditorFullscreen(false);
        setContainerData(prev => {
            const out = { ...prev, ...captured, editor: next };
            ['NL', 'EN', 'FR'].forEach((U) => {
                if (designRowCount(captured[`design${U}`]) > 0) {
                    out[`text${U}`] = captured[`html${U}`] || '';
                    out[`hadDesign${U}`] = true;
                }
            });
            return out;
        });
    };

    const saveContainer = async () => {
        const designs = await captureEditorDesigns();
        const finalData = { ...containerData, ...designs };
        // The body only goes to the shop when the dialog really held it: the
        // stored text was loaded (or there was none to load).
        if (containerBodyStatus === 'ok') finalData.bodyTouched = true;
        ['NL', 'EN', 'FR'].forEach((U) => {
            if (designRowCount(finalData[`design${U}`]) > 0) finalData[`hadDesign${U}`] = true;
        });

        if (editingContainerId) {
            const updater = (list) => list.map(i => (i.id === editingContainerId
                ? { ...i, name: finalData.reference || 'Container', config: finalData }
                : i));
            setAvailable(updater);
            setAvailable404(updater);
            setActive(updater);
            setActive404(updater);
        } else {
            const newContainer = {
                id: `container_${Date.now()}`,
                name: finalData.reference || 'New container',
                isContainer: true,
                configurable: true,
                config: finalData,
            };
            // New containers are available on both the Front page and the 404 page.
            setAvailable(prev => [...prev, newContainer]);
            setAvailable404(prev => [...prev, newContainer]);
        }
        setContainersDirty(true);
        setSaveAfterContainer(true);
        closeContainerModal();
    };

    const deleteContainer = (id) => {
        setAvailable(prev => prev.filter(i => i.id !== id));
        setAvailable404(prev => prev.filter(i => i.id !== id));
        setActive(prev => prev.filter(i => i.id !== id));
        setActive404(prev => prev.filter(i => i.id !== id));
        setContainersDirty(true);
    };

    // Every distinct container block currently placed on the Front page or 404
    // page (deduped by id) — the source of truth for the container save-diff.
    const containerBlocks = () => {
        const seen = new Map();
        [...active, ...available, ...active404, ...available404].forEach(bk => {
            if (bk && bk.isContainer && !seen.has(bk.id)) seen.set(bk.id, bk);
        });
        return [...seen.values()];
    };

    const updateContainerField = (field, value) => {
        setContainerData(prev => ({ ...prev, [field]: value }));
    };
    // Shop row id of the container open in the dialog (null for a new one).
    const editingBlockDbId = () => {
        const bk = [...active, ...available, ...active404, ...available404].find(b => b && b.id === editingContainerId);
        return bk ? containerDbId(bk) : null;
    };

    const getEditorRef = (lang) => {
        if (lang === 'NL') return editorRefNL;
        if (lang === 'EN') return editorRefEN;
        if (lang === 'FR') return editorRefFR;
        return null;
    };

    const copyEditorDesign = (fromLang, toLang) => {
        const fromRef = getEditorRef(fromLang);
        const toRef = getEditorRef(toLang);
        if (!fromRef?.current?.editor || !toRef?.current?.editor) return;
        fromRef.current.editor.saveDesign((design) => {
            toRef.current.editor.loadDesign(design);
            updateContainerField(`design${toLang}`, design);
        });
    };

    const onEditorReady = (lang) => () => {
        const ref = getEditorRef(lang);
        const design = containerData[`design${lang}`];
        if (design && ref?.current?.editor) {
            try {
                ref.current.editor.loadDesign(design);
            } catch (e) { /* ignore */ }
        }
    };

    // Screen-wide translate helper (the section modals have their own local
    // copy inside renderSectionModalFields; this one serves the main render).
    const trs = (key, fb) => (props.t ? props.t(key, fb) : fb);
    const isDark = props.theme === 'dark';
    const panelBg = isDark ? '#22272e' : '#f7f8fa';
    const cardBg = isDark ? '#2d333b' : '#ffffff';
    const borderColor = isDark ? '#3a3f47' : '#e4e7eb';
    const textColor = isDark ? Colors.dark_text : Colors.light_text;
    const mutedText = isDark ? '#b0b8c1' : '#768390';

    // Sub-tab bar styling — "inverted selection" look: the whole band takes
    // the selected-blue (primary = sft_blue, the same blue the active MAIN tab
    // uses), and the tabs sit flat on top of it — inactive labels in a soft
    // grey, the active one in solid white with a white indicator. This makes
    // the sub-tabs read as one connected toolbar belonging to the active main
    // tab. The main tab bar's bottom border is removed so the white main tabs
    // flow straight into this blue band.
    // Sub-tab bar: blue band (the same blue an active MAIN tab uses for its
    // text). The selected sub-tab is made to LOOK like a selected main tab —
    // blue text — which on the blue band needs a white surface behind it. So
    // the active sub-tab becomes a white tab with blue text (like the active
    // tab in a coloured tab strip); inactive tabs are soft white on the blue.
    // The band itself has no padding; rounded top corners make it read as a
    // panel attached under the main tabs.
    const subTabBarSx = {
        mb: 2, mx: -3,
        backgroundColor: Colors.sft_blue,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
    };
    const subTabsSx = {
        // Belt-and-braces: also paint the Tabs root blue so the band is solid
        // even where the Box behind it might not show through. It must carry
        // the same rounded top corners as the wrapping Box, otherwise its
        // square blue rectangle paints over and hides the Box's radius.
        backgroundColor: Colors.sft_blue,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        // The white active tab conveys selection — no underline needed.
        '& .MuiTabs-indicator':        { display: 'none' },
        // Spacing lives on the items (margin), not the band. Only the TOP
        // corners are rounded and there's no bottom margin, so the active
        // white tab reads like a real tab whose flat bottom connects down to
        // the content — framed by blue on the top and sides.
        '& .MuiTab-root':              { color: 'rgba(255,255,255,0.78)', borderTopLeftRadius: '6px', borderTopRightRadius: '6px', mt: 0.75, mx: 0.5 },
        // MUI forces selected → primary.main with high specificity; override
        // with !important so the blue text sits on the white tab surface.
        '& .MuiTab-root.Mui-selected': { color: `${Colors.sft_blue} !important`, backgroundColor: cardBg, fontWeight: 700 },
        '& .MuiTabs-scrollButtons':    { color: 'rgba(255,255,255,0.78)' },
    };

    const blockLabel = (item) => {
        // Product-container blocks are labelled by their `reference` (the internal
        // identifier the admin set) so the label is STABLE across save/reload and
        // matches what's shown while creating. Falls back to the localized title,
        // then the frozen name, when no reference is set.
        if (item?.isContainer && item.container) {
            const c = item.container;
            const l = String(props.lang || i18n.language || 'nl').slice(0, 2).toLowerCase();
            // The reference is stored in the DB `description` column (see
            // containerRowToFormData: reference ← row.description).
            return c.description || c[`loc_${l}`] || c.loc_nl || c.loc_en || c.loc_fr || item.name;
        }
        return (props.t && item.i18nKey ? props.t(item.i18nKey, item.name) : item.name);
    };

    // Bundle passed to the module-scope block-board components (see
    // config/blockBoard.js). Plain per-render object: the point of the hoist is
    // the STABLE COMPONENT IDENTITY (no row remounts / DnD re-registration per
    // render) — row re-renders themselves are cheap.
    // Closing the block-config dialog WITHOUT saving discards the pending
    // image removals (the × in the carousel/grid editors only sets a
    // pendingRemove flag; the real DELETE runs on Save). Stripping on close
    // makes Annuleren behave as an undo for those flags, so a re-open shows
    // the image again. Other edits keep following the global dirty model.
    const stripPendingImageRemoves = (cfg, listKey) => {
        const stripImg = (img) => {
            if (!img || !img.pendingRemove) return img;
            const { pendingRemove, ...rest } = img;
            return rest;
        };
        const lists = cfg[listKey] || {};
        let changed = false;
        const next = Object.fromEntries(Object.entries(lists).map(([lang, list]) => [
            lang,
            (list || []).map((row) => {
                if (!row.desktopImage?.pendingRemove && !row.alternativeImage?.pendingRemove) return row;
                changed = true;
                return { ...row, desktopImage: stripImg(row.desktopImage), alternativeImage: stripImg(row.alternativeImage) };
            }),
        ]));
        return changed ? { ...cfg, [listKey]: next } : cfg;
    };
    const closeConfigBlockDialog = () => {
        setCarouselConfig((c) => stripPendingImageRemoves(c, 'slides'));
        setImageGridConfig((c) => stripPendingImageRemoves(c, 'cells'));
        setConfigBlock(null);
    };

    const blockBoard = {
        cardBg, borderColor, panelBg, isDark, mutedText, textColor,
        blockLabel, openEditContainerModal, deleteContainer, setConfigBlock,
        openNewContainerModal,
    };

    const activeItem = activeId
        ? [...available, ...available404, ...active, ...active404].find(i => i.id === activeId)
        : null;

    // Features sub-tabs render placeholders for now — the underlying schema
    // for each (Gift voucher / Shop the look / Brands / Banners / Algemeen)
    // is still being defined. Slots are reserved here so the routing stays
    // stable while we wire each one to real backend settings.
    const renderFeaturesTab = () => (
        <>
            <Box sx={subTabBarSx}>
                <Tabs
                    value={featuresTab}
                    onChange={(_e, v) => startTransition(() => setFeaturesTab(v))}
                    variant="scrollable"
                    scrollButtons="auto"
                    className={props.theme}
                    sx={subTabsSx}
                >
                    <Tab label={props.t ? props.t('cfg_features_tab_cadeaubon', 'Cadeaubon')     : 'Cadeaubon'}     icon={<CardGiftcardIcon />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                    <Tab label={props.t ? props.t('cfg_features_tab_stl',       'Shop the look') : 'Shop the look'} icon={<CheckroomIcon />}    iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                    <Tab label={props.t ? props.t('cfg_features_tab_banners',   'Banners')       : 'Banners'}       icon={<ViewCarouselIcon />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                    <Tab label={props.t ? props.t('cfg_features_tab_overige',   'Overige')       : 'Overige'}       icon={<InfoOutlinedIcon />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                </Tabs>
            </Box>
            {configLoading ? renderConfigSkeleton() : (<>
            <TabPanel value={featuresTab} index={0}>{() => (<><CadeaubonTab    theme={props.theme} t={props.t} config={cadeaubonConfig} onConfigChange={setCadeaubonConfig} placeholders={cadeaubonPlaceholders} onPlaceholdersChange={setCadeaubonPlaceholders} layouts={cadeaubonLayouts} onLayoutsChange={setCadeaubonLayouts} chequesPage={staticPages.cheques} onChequesPageChange={setStaticPage('cheques')} login={props.login} api={props.api} selectedWebshop={props.selectedWebshop} /></>)}</TabPanel>
            <TabPanel value={featuresTab} index={1}>{() => (<><ShopTheLookTab  theme={props.theme} t={props.t} config={looksConfig} onConfigChange={setLooksConfig} shopFieldLabels={shopFieldLabels} /></>)}</TabPanel>
            <TabPanel value={featuresTab} index={2}>{() => (<><BannersTab      theme={props.theme} t={props.t} banners={banners} onBannersChange={setBanners} /></>)}</TabPanel>
            <TabPanel value={featuresTab} index={3}>{() => (<><OverigeTab      theme={props.theme} t={props.t} config={overigeConfig} onConfigChange={setOverigeConfig} /></>)}</TabPanel>
            </>)}
        </>
    );

    // Items whose .onlyOn === 'page404' should only surface on the 404 tab's
    // Available column. Container/sales items (no onlyOn flag) show on both.
    const availableForFrontPage = available.filter(i => i.onlyOn !== 'page404');
    const availableForPage404   = available404.filter(i => !i.onlyOn || i.onlyOn === 'page404');

    const renderFrontPageTab = () => (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            {/* One slim row: the landing page on/off switch (SHOW_LANDING; off =
                the home route redirects to the explorer, the blocks stay
                editable and saved) + the way back to the visual mockup
                (Opmaak > Landingspagina has "Blokken beheren" to come here). */}
            <LandingActiveBar
                active={showLanding}
                onChange={setShowLanding}
                theme={props.theme}
                t={props.t}
                action={can('webshop.config.tab.style') && (
                    <Button
                        size="small"
                        startIcon={<PaletteIcon fontSize="small" />}
                        onClick={() => startTransition(() => { setTabValue(1); setStyleTab(2); })}
                        sx={{ textTransform: 'none' }}
                    >
                        {props.t ? props.t('ui_landing_view_in_style', 'Bekijk in Opmaak') : 'Bekijk in Opmaak'}
                    </Button>
                )}
            />
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start', opacity: showLanding ? 1 : 0.55, transition: 'opacity 0.15s' }}>
                <DroppableColumn board={blockBoard} column="available" items={availableForFrontPage} label={props.t ? props.t('cfg_block_available', 'Available') : 'Available'} />
                <DroppableColumn board={blockBoard} column="active" items={active} label={props.t ? props.t('cfg_block_active', 'Active') : 'Active'} />
            </Box>
            <DragOverlay>
                {activeItem ? (
                    <BlockRowOverlay board={blockBoard} item={activeItem} column={findContainer(activeId)} />
                ) : null}
            </DragOverlay>
        </DndContext>
    );

    // 404 page — same DnD as Front page but operates on its OWN `active404` +
    // `available404` lists, so composing the 404 page never affects the Front
    // page (landing) catalog. Per-container settings still differ because each
    // list entry carries its own .config.
    const renderPage404Tab = () => (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                <DroppableColumn board={blockBoard} column="available" items={availableForPage404} label={props.t ? props.t('cfg_block_available', 'Available') : 'Available'} />
                <DroppableColumn board={blockBoard} column="active" items={active404} label={props.t ? props.t('cfg_block_active', 'Active') : 'Active'} />
            </Box>
            <DragOverlay>
                {activeItem ? (
                    <BlockRowOverlay board={blockBoard} item={activeItem} column={findContainer(activeId)} />
                ) : null}
            </DragOverlay>
        </DndContext>
    );

    // Stable context value: an inline literal gave every language consumer a
    // new identity per screen render (so per keystroke), forcing all
    // per-language editors to re-render through any memo boundary.
    const languagesCtxValue = useMemo(() => ({
        adminLanguages:   styleSettings.visibility.adminLanguages,
        visibleLanguages: styleSettings.visibility.visibleLanguages,
    }), [styleSettings.visibility.adminLanguages, styleSettings.visibility.visibleLanguages]);

    // Container body (text / own HTML / designer): only the languages that are
    // editable in the admin (ADMIN_LANGUAGES) get a tab or a copy button, like
    // the title/button fields. editorLang stays the index in CONTAINER_LANGS so
    // the per-language editors keep their refs; the hidden ones stay mounted,
    // so their content goes along unchanged on save.
    const containerLangIdx = useMemo(() => {
        const admin = styleSettings.visibility.adminLanguages || ALL_LANGUAGE_CODES;
        const idx = CONTAINER_LANGS.map((_c, i) => i).filter(i => admin.includes(CONTAINER_LANGS[i]));
        return idx.length ? idx : [0];
    }, [styleSettings.visibility.adminLanguages]);
    useEffect(() => {
        if (!containerLangIdx.includes(editorLang)) setEditorLang(containerLangIdx[0]);
    }, [containerLangIdx, editorLang]);

    // --- Style tab helpers ---
    // Stable identity (only touches a ref) so ColorField's React.memo isn't
    // defeated by a fresh function on every screen render.
    const markPickerInteraction = useCallback(() => {
        colorPickerInteractionRef.current = Date.now();
    }, []);
    // colorFieldProps is spread on each <ColorField .../> usage — passing them
    // as stable theme values keeps the module-level memo component stable.
    const colorFieldProps = { mutedText, borderColor, markPickerInteraction };

    // Sidebar state also drives the modal sizing of the block-config and
    // container dialogs below (webshopModalSizing collapsed flag).
    const sidebarCollapsed = !!props.hideSidebar;

    // the dialog frames (block config, product containers, style modals) --
    // their bodies go through DialogBody and pick the modal look up there
    const { tokens: variantTokens } = useModalDesign();
    const { renderStyleTab } = buildStyleRenderers({
        variantTokens,
        activeStyleSection, setActiveStyleSection, openStyleModal, styleSettings,
        updateStyleSection, updateStyleGlobal, updateStyleVisibility, globalWidths,
        updateGlobalWidths, navigationConfig, updateNavigation, footerConfig,
        updateFooter, extraFooterConfig, updateExtraFooter, headerLogoConfig,
        updateHeaderLogo, uploadHeaderImage, imageGalleryConfig, updateImageGallery,
        groupsFilterConfig, setGroupsFilterConfig, productCardConfig, setProductCardConfig,
        productGridConfig, setProductGridConfig, productDetailInfoConfig,
        updateProductDetailInfo, staticPages, setStaticPage, navbarOrder, setNavbarOrder,
        navFilterStyle, googleFonts, shopFieldLabels, textTypeOptions, setConfigBlock, setPagesTab,
        setTabValue, saveConfig, configSaving, colorFieldProps, colorPickerInteractionRef,
        isDark, cardBg, borderColor, textColor, mutedText, props,
        active, carouselConfig, imageGridConfig, showLanding, setShowLanding,
        // same cog behaviour + labels as the draggable blocks on Pagina's
        openEditContainerModal, blockLabel,
        // footer address link: coordinates looked up like the store addresses
        geocodeViaShop, gmapsApiKey,
    });

    const renderShortcutsTab = () => (
        <Box sx={{ maxWidth: 720, py: 4 }}>
            <Typography variant="h6" sx={{ color: textColor, mb: 1 }}>Shortcuts</Typography>
            <Typography variant="body2" sx={{ color: mutedText }}>
                Keyboard shortcuts and quick-access links for admin actions. Not wired up yet.
            </Typography>
        </Box>
    );


    // Aggregate dirty flag across every wired config block — drives both the
    // floating save button and the new header save button so they stay in
    // sync. `canSave` also guards against saving while loading or with no
    // shop selected.
    const configDirty = orderDirty || showLandingDirty || productGridDirty || productCardDirty || styleSettingsDirty ||
        groupsFilterDirty || globalWidthsDirty || headerLogoDirty || navigationDirty ||
        footerDirty || extraFooterDirty || imageGalleryDirty ||
        productDetailInfoDirty || carouselDirty || imageGridDirty || staticPagesDirty || storesDirty || separatorDirty || mapSettingsDirty || cardsDirty || brandsConfigDirty || dynamicPagesDirty || bannersDirty || looksConfigDirty || overigeConfigDirty || cadeaubonConfigDirty || cadeaubonPlaceholdersDirty || cadeaubonLayoutsDirty || processingConfigDirty || customerGroupsDirty || syncConfigDirty || order404Dirty || navbarOrderDirty || containersDirty || brandContentDirty;
    const canSave = !!props.selectedWebshop && !configLoading && !configError && !configSaving && configDirty;

    // Unsaved-changes guard — the sidebar navigation / webshop picker /
    // browser-close consult this registry before throwing the state away.
    // The check returns LABELS of the dirty sections so the confirmation
    // dialog can show exactly what would be lost. A ref keeps the registered
    // check reading the LIVE list (the register call itself runs once).
    const lbl = (k, fb) => (props.t ? props.t(k, fb) : fb);
    // Where each save section lives, keyed like config/saveSections.js. One
    // table for both the unsaved-changes list and the save report, so a
    // section is named the same in both. `icon` maps to the SAME icon its
    // main tab uses. Sections that share a label (the three Cadeaubon parts,
    // Verwerking + klantvelden, winkels + kaart) show up as one item.
    const saveSectionMeta = (key) => {
        const P = lbl('cfg_main_pages',    "Pagina's");
        const S = lbl('cfg_main_style',    'Opmaak');
        const F = lbl('cfg_main_features', 'Features');
        const processing = ['processing', lbl('cfg_main_processing', 'Verwerking'), ''];
        const cadeaubon  = ['features', F, lbl('cfg_features_tab_cadeaubon', 'Cadeaubon')];
        const stores     = ['stores', lbl('unsaved_about_stores', 'Over ons / winkels'), ''];
        const table = {
            order:         ['pages', P, lbl('cfg_pages_tab_front',   'Startpagina')],
            showLanding:   ['pages', P, lbl('cfg_pages_landing_active', 'Landingspagina activeren')],
            order404:      ['pages', P, lbl('cfg_pages_tab_404',     '404-pagina')],
            carousel:      ['pages', P, lbl('cfg_block_carousel',   'Carousel')],
            imageGrid:     ['pages', P, lbl('cfg_block_image_grid', 'Afbeeldingen-grid')],
            staticPages:   ['pages', P, `${lbl('cfg_pages_tab_about', 'About')} / ${lbl('cfg_pages_tab_disclaimer', 'Terms & conditions')} / Privacy / 404 / ${lbl('cfg_block_intro', 'Welkom')}`],
            dynamicPages:  ['pages', P, lbl('cfg_pages_tab_dynamic', 'Dynamische pagina')],
            brandsConfig:  ['pages', P, lbl('cfg_pages_tab_merken',  'Merken')],
            brandContent:  ['pages', P, lbl('cfg_brandcontent_title', 'Merken-content (SEO & banners)')],
            cards:         ['pages', P, lbl('cfg_pages_tab_cart',    'Winkelmand')],
            containers:    ['pages', P, lbl('cfg_container_new_title', 'Productcontainer')],
            productGrid:   ['style', S, lbl('cfg_style_section_grid', 'Product grid')],
            productCard:   ['style', S, lbl('cfg_style_section_productCard', 'Product card')],
            styleSettings: ['style', S, lbl('cfg_style_global_title', 'Globaal')],
            groupsFilter:  ['style', S, lbl('cfg_style_section_groups', 'Filters & sortering')],
            // The button radius sits in the Globaal panel but is stored with the widths.
            globalWidths:  ['style', S, `${lbl('cfg_widths_title', 'Globale breedtes')} / ${lbl('cfg_style_global_button_radius', 'Knopafronding')}`],
            headerLogo:    ['style', S, lbl('cfg_style_section_header', 'Header (logo)')],
            navigation:    ['style', S, lbl('cfg_style_section_menu', 'Menubalk')],
            navbarOrder:   ['style', S, lbl('cfg_nav_sorting_title', 'Weergavevolgorde filters')],
            footer:        ['style', S, lbl('cfg_style_section_footer', 'Footer')],
            extraFooter:   ['style', S, lbl('cfg_style_section_footerExtra', 'Footer (extra)')],
            imageGallery:  ['style', S, lbl('cfg_style_section_image', 'Afbeeldingengalerij')],
            pdi:           ['style', S, lbl('cfg_style_section_productDetailInfo', 'Product detail')],
            banners:       ['features', F, lbl('cfg_features_tab_banners', 'Banners')],
            looks:         ['features', F, lbl('cfg_features_tab_stl', 'Shop the look')],
            overige:       ['features', F, lbl('cfg_features_tab_overige', 'Overige')],
            cadeaubonConfig: cadeaubon, cadeaubonPlaceholders: cadeaubon, cadeaubonLayouts: cadeaubon,
            processing, customerGroups: processing,
            sync:          ['sync', lbl('cfg_main_sync', 'Synchronisatie'), ''],
            stores, map: stores,
        };
        const m = table[key] || ['', '', lbl('save_report_section_other', 'Onbekend onderdeel')];
        return { icon: m[0], tab: m[1], section: m[2] };
    };
    const unsavedItems = [];
    // Only report unsaved changes once the screen has actually loaded. During the
    // async load the baseline refs (originalXxxRef.current) are set synchronously
    // while the matching setState is still pending, so for a render or two the
    // dirty useMemos read "default state ≠ loaded ref" = dirty. Without this gate,
    // clicking away mid-load pops the unsaved-changes modal though nothing was
    // edited. `canSave` already guards on !configLoading the same way.
    // `!configError` matters: when the mini-api load fails the effect returns
    // early (before the per-section baseline refs are set), so the dirty
    // useMemos read default-state ≠ unset-ref = dirty and would pop a FALSE
    // "unsaved changes" warning on a shop the user never edited.
    if (!configLoading && !configError && !!props.selectedWebshop) {
        // In the order the tabs appear; a label shared by several sections is
        // listed once.
        const dirtyInOrder = [
            ['order', orderDirty], ['showLanding', showLandingDirty], ['order404', order404Dirty],
            ['carousel', carouselDirty], ['imageGrid', imageGridDirty], ['staticPages', staticPagesDirty],
            ['dynamicPages', dynamicPagesDirty], ['brandsConfig', brandsConfigDirty], ['brandContent', brandContentDirty],
            ['cards', cardsDirty], ['containers', containersDirty],
            ['productGrid', productGridDirty], ['productCard', productCardDirty], ['styleSettings', styleSettingsDirty],
            ['groupsFilter', groupsFilterDirty], ['globalWidths', globalWidthsDirty], ['headerLogo', headerLogoDirty],
            ['navigation', navigationDirty], ['navbarOrder', navbarOrderDirty], ['footer', footerDirty],
            ['extraFooter', extraFooterDirty], ['imageGallery', imageGalleryDirty], ['pdi', productDetailInfoDirty],
            ['banners', bannersDirty], ['looks', looksConfigDirty], ['overige', overigeConfigDirty],
            ['cadeaubonConfig', cadeaubonConfigDirty || cadeaubonPlaceholdersDirty || cadeaubonLayoutsDirty],
            ['processing', processingConfigDirty || customerGroupsDirty],
            ['sync', syncConfigDirty],
            ['stores', storesDirty || separatorDirty || mapSettingsDirty],
        ];
        for (const [key, dirty] of dirtyInOrder) {
            if (dirty) unsavedItems.push(saveSectionMeta(key));
        }
    }
    const unsavedItemsRef = useRef([]);
    unsavedItemsRef.current = unsavedItems;
    // Live ref to saveConfig so the guard's "Save & continue" calls the current
    // closure (the register effect runs once on mount).
    const saveConfigRef = useRef(null);
    saveConfigRef.current = saveConfig;
    useEffect(() => {
        registerUnsavedCheck(
            'webshop-config',
            () => unsavedItemsRef.current,
            () => saveConfigRef.current && saveConfigRef.current(),
        );
        return () => unregisterUnsavedCheck('webshop-config');
    }, []);

    // Loading placeholder: card-shaped skeletons while the webshop's config is
    // being fetched, so the screen reads as "loading" instead of flashing empty
    // defaults before the real data lands.
    const renderConfigSkeleton = () => (
        <Box sx={{ pt: 3 }}>
            <Stack spacing={2}>
                {[0, 1, 2].map(i => (
                    <Paper key={i} elevation={0} sx={{ borderRadius: 2, p: 2.5, border: `1px solid ${borderColor}`, backgroundColor: cardBg }}>
                        <Skeleton variant="text" width="32%" height={28} />
                        <Skeleton variant="text" width="55%" height={18} sx={{ mb: 2 }} />
                        <Skeleton variant="rounded" height={40} sx={{ mb: 1.5, borderRadius: 1 }} />
                        <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1 }} />
                    </Paper>
                ))}
            </Stack>
        </Box>
    );

    // Gate the whole editor on a usable webshop. No row → "no webshop linked";
    // mini-api load failed → the (now visible) reason. Both replace the tabs
    // entirely so the user can't edit empty defaults that silently no-op on
    // save. Hooks above already ran, so these conditional returns are safe.
    if (!props.selectedWebshop || (configError && !configLoading)) {
        return (
            <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
                <Box sx={{ backgroundColor: cardBg, borderRadius: 2, p: 3, border: `1px solid ${borderColor}` }}>
                    {!props.selectedWebshop
                        ? <NoWebshopState t={props.t} api={props.api} onProvisioned={props.onProvisioned} notify={props.notify} theme={props.theme} />
                        : <WebshopErrorState error={configError} t={props.t} lang={props.lang} />}
                </Box>
            </div>
        );
    }

    return (
        <LanguagesProvider value={languagesCtxValue}>
        <ShopDocumentImportProvider api={props.api} login={props.login} webshopId={props.selectedWebshop?.id}>
        <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
            <Box sx={{ backgroundColor: cardBg, borderRadius: 2, px: 3, pb: 3, pt: 0, border: `1px solid ${borderColor}` }}>

                {/* No bottom border here — the white main-tab row flows
                    straight into the tinted sub-tab tray below, so the two
                    read as one connected header instead of two stacked bars. */}
                <Box sx={{
                    mx: -3,
                    display: 'flex', alignItems: 'center', gap: 2,
                    pr: 3,
                    // Same surface colour the tabs sit on (the `.light`/`.dark`
                    // class uses var(--light_basic)/var(--dark_basic)), so the
                    // search + save area no longer reads as a separate white
                    // block. Rounded top corners match the card.
                    backgroundColor: isDark ? 'var(--dark_basic)' : 'var(--light_basic)',
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                }}>
                    <Tabs value={tabValue} onChange={handleTabChange} className={props.theme} sx={{ flex: 1, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTab-root.Mui-selected': { fontWeight: 700 } }}>
                        {can('webshop.config.tab.pages')      && <Tab value={0} label={props.t ? props.t('cfg_main_pages',      'Pages')          : 'Pages'}          icon={<ArticleIcon />}         iconPosition="top" />}
                        {can('webshop.config.tab.style')      && <Tab value={1} label={props.t ? props.t('cfg_main_style',      'Style')          : 'Style'}          icon={<PaletteIcon />}         iconPosition="top" />}
                        {can('webshop.config.tab.features')   && <Tab value={2} label={props.t ? props.t('cfg_main_features',   'Features')       : 'Features'}       icon={<InfoOutlinedIcon />}    iconPosition="top" />}
                        {can('webshop.config.tab.processing') && <Tab value={3} label={props.t ? props.t('cfg_main_processing', 'Processing')     : 'Processing'}     icon={<ShoppingBasketIcon />}  iconPosition="top" />}
                        {can('webshop.config.tab.sync')       && <Tab value={4} label={props.t ? props.t('cfg_main_sync',       'Sync-settings')  : 'Sync-settings'}  icon={<SyncIcon />}            iconPosition="top" />}
                        {/* Shortcuts tab hidden for now — re-enable once it has real content. */}
                    </Tabs>
                    <WebshopSearchBar search={search} />
                    {/* Persistent save in the header, to the right of the search
                        field. Mirrors the floating save button's behaviour.
                        Sized to roughly match the 260px search field so the two
                        read as a balanced pair. */}
                    <Button
                        ref={headerSaveRef}
                        variant="contained"
                        onClick={saveConfig}
                        disabled={!canSave}
                        sx={{ flexShrink: 0, textTransform: 'none', borderRadius: 1.5, minWidth: 220, height: 40 }}
                    >
                        {props.t ? props.t('save_changes', 'Wijzigingen opslaan') : 'Wijzigingen opslaan'}
                    </Button>
                </Box>

                {/* Floating save — only when there are unsaved changes AND the
                    header save button has scrolled out of view, so long pages
                    can always be saved. Portaled to <body> so position:fixed is
                    relative to the viewport (the screen scrolls in an inner
                    container whose ancestors may create a containing block). */}
                {configDirty && !headerSaveVisible && createPortal(
                    <Fab
                        variant="extended"
                        color="primary"
                        onClick={saveConfig}
                        disabled={!canSave}
                        sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300, textTransform: 'none' }}
                    >
                        {configSaving
                            ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                            : <SaveIcon sx={{ mr: 1 }} />}
                        {props.t ? props.t('save_changes', 'Wijzigingen opslaan') : 'Wijzigingen opslaan'}
                    </Fab>,
                    document.body,
                )}

                {/* Reminder of a Save that did not save everything — stays until
                    a later Save is clean or nothing is left to save. */}
                {saveReport && !saveReport.ok && configDirty && !saveReportOpen && (() => {
                    const failedCount = new Set(saveReport.failed.map(f => {
                        const m = saveSectionMeta(f.section);
                        return `${m.tab}|${m.section}`;
                    })).size;
                    return (
                        <Alert
                            severity="error"
                            data-save-report="banner"
                            sx={{ mx: -3, borderRadius: 0, alignItems: 'center', '& .MuiAlert-message': { py: 0.5 } }}
                            action={(
                                <Button color="inherit" size="small" onClick={() => setSaveReportOpen(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                    {lbl('save_report_banner_action', 'Bekijk')}
                                </Button>
                            )}
                        >
                            {saveReport.unexpected
                                ? lbl('save_report_title_unexpected', 'Opslaan is onderbroken')
                                : failedCount === 1
                                    ? lbl('save_report_banner_one', 'Bij de laatste opslag is 1 onderdeel niet opgeslagen.')
                                    : `${lbl('save_report_banner_many_a', 'Bij de laatste opslag zijn')} ${failedCount} ${lbl('save_report_banner_many_b', 'onderdelen niet opgeslagen.')}`}
                        </Alert>
                    );
                })()}
                <SaveReportDialog
                    open={saveReportOpen}
                    report={saveReport}
                    sectionMeta={saveSectionMeta}
                    onClose={() => setSaveReportOpen(false)}
                    onRetry={() => { setSaveReportOpen(false); saveConfig(); }}
                    saving={configSaving}
                    t={props.t}
                    theme={props.theme}
                />

                <Suspense fallback={renderConfigSkeleton()}>
                <TabPanel value={tabValue} index={2} sx={{ pt: 0 }}>{() => (<>{renderFeaturesTab()}</>)}</TabPanel>
                <TabPanel value={tabValue} index={0} sx={{ pt: 0 }}>{() => (<>
                    {/* Pages: groups every page-level composition under one tab so
                        the top bar stays compact. Front page + 404 + Cart used to
                        live as their own top-level tabs; About/Disclaimer/Privacy/
                        Dynamic are placeholders for now. pt:0 on the outer panel
                        so the sub-tabs hug the main tabs without a gap. */}
                    <Box sx={subTabBarSx}>
                        <Tabs value={pagesTab} onChange={(_e, v) => startTransition(() => setPagesTab(v))} variant="scrollable" scrollButtons="auto" className={props.theme} sx={subTabsSx}>
                            <Tab label={props.t ? props.t('cfg_pages_tab_front',      'Front page')   : 'Front page'}   icon={<GridViewIcon />}            iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_pages_tab_404',        '404 page')     : '404 page'}     icon={<ReportGmailerrorredIcon />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_pages_tab_merken',     'Merken')       : 'Merken'}       icon={<LocalOfferIcon />}          iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_pages_tab_cart',       'Cart page')    : 'Cart page'}    icon={<ShoppingCartIcon />}        iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_pages_tab_about',      'About')        : 'About'}        icon={<DescriptionIcon />}         iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_pages_tab_disclaimer', 'Terms & conditions')   : 'Terms & conditions'}   icon={<GavelIcon />}               iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_pages_tab_privacy',    'Privacy')      : 'Privacy'}      icon={<LockIcon />}                iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_pages_tab_dynamic',    'Dynamic page') : 'Dynamic page'} icon={<DynamicFeedIcon />}         iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                        </Tabs>
                    </Box>
                    {configLoading ? renderConfigSkeleton() : (<>
                    <TabPanel value={pagesTab} index={0}>{() => (<>{renderFrontPageTab()}</>)}</TabPanel>
                    <TabPanel value={pagesTab} index={1}>{() => (<>{renderPage404Tab()}</>)}</TabPanel>
                    <TabPanel value={pagesTab} index={2}>{() => (<><MerkenTab       theme={props.theme} t={props.t} config={brandsConfig} onConfigChange={setBrandsConfig} shopFieldLabels={shopFieldLabels} /><Box sx={{ mt: 2 }}><BrandContentCard theme={props.theme} t={props.t} api={props.api} login={props.login} selectedWebshop={props.selectedWebshop} sessionRef={brandContentSessionRef} onDirtyChange={setBrandContentDirty} saveRef={brandContentSaveRef} /></Box></>)}</TabPanel>
                    <TabPanel value={pagesTab} index={3}>{() => (<>
                        <CartPageTab theme={props.theme} t={props.t} cards={cards} onCardsChange={setCards}
                            layout={cartLayout} onLayoutChange={setCartLayout} containerOptions={cartContainerOptions} shopFieldLabels={shopFieldLabels} />
                    </>)}</TabPanel>
                    <TabPanel value={pagesTab} index={4}>{() => (<>
                        <AboutPageTab
                            theme={props.theme} t={props.t}
                            value={staticPages.about}
                            onChange={setStaticPage('about')}
                            stores={stores}
                            onStoresChange={setStores}
                            hoursSeparator={hoursSeparator}
                            onHoursSeparatorChange={setHoursSeparator}
                            gmapsApiKey={gmapsApiKey}
                            geocode={geocodeViaShop}
                            mapSettings={mapSettings}
                            onMapSettingsChange={setMapSettings}
                        />
                    </>)}</TabPanel>
                    <TabPanel value={pagesTab} index={5}>{() => (<>
                        <StaticPageTab
                            theme={props.theme} t={props.t}
                            pageKey="disclaimer"
                            defaultTitle="Terms & conditions"
                            defaultDescription="This text appears on the Terms & conditions page."
                            value={staticPages.disclaimer}
                            onChange={setStaticPage('disclaimer')}
                        />
                    </>)}</TabPanel>
                    <TabPanel value={pagesTab} index={6}>{() => (<>
                        <StaticPageTab
                            theme={props.theme} t={props.t}
                            pageKey="privacy"
                            defaultTitle="Privacy"
                            defaultDescription="This text appears on the general Privacy page."
                            value={staticPages.privacy}
                            onChange={setStaticPage('privacy')}
                        />
                    </>)}</TabPanel>
                    <TabPanel value={pagesTab} index={7}>{() => (<>
                        <DynamicPageTab
                            theme={props.theme} t={props.t}
                            baseUrl={shopImageBase(props.selectedWebshop?.display_name)}
                            pages={dynamicPages}
                            onPagesChange={setDynamicPages}
                            fetchDesign={fetchDynDesign}
                            fetchBody={fetchDynBody}
                        />
                    </>)}</TabPanel>
                    </>)}
                </>)}</TabPanel>
                <TabPanel value={tabValue} index={1} sx={{ pt: 0 }}>{() => (<>
                    {/* Style: Explorer + Product detail share most settings
                        (language bar, header, menu, footer) so editing one
                        propagates to the other automatically — both panels
                        read from the same styleSettings state. */}
                    <Box sx={subTabBarSx}>
                        <Tabs value={styleTab} onChange={(_e, v) => startTransition(() => setStyleTab(v))} className={props.theme} sx={subTabsSx}>
                            {/* Display order: landing page first (it is the first page a
                                visitor sees). The tab VALUES stay 0=Explorer, 1=Product
                                detail, 2=Landing -- the search routes (sub.styleTab) and
                                "Bekijk in Opmaak" address them by value. */}
                            {/* the landing blocks from Pagina's, drawn as a rough mockup */}
                            <Tab value={2} label={props.t ? props.t('cfg_pages_tab_front',          'Front page')     : 'Front page'}     sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab value={0} label={props.t ? props.t('cfg_style_tab_explorer',       'Explorer')       : 'Explorer'}       sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab value={1} label={props.t ? props.t('cfg_style_tab_product_detail', 'Product detail') : 'Product detail'} sx={{ minHeight: 48, textTransform: 'none' }} />
                        </Tabs>
                    </Box>
                    {configLoading ? renderConfigSkeleton() : (<>
                    <TabPanel value={styleTab} index={0} sx={{ pt: 0 }}>{() => (<>{renderStyleTab('explorer')}</>)}</TabPanel>
                    <TabPanel value={styleTab} index={1} sx={{ pt: 0 }}>{() => (<>{renderStyleTab('productDetail')}</>)}</TabPanel>
                    <TabPanel value={styleTab} index={2} sx={{ pt: 0 }}>{() => (<>{renderStyleTab('landing')}</>)}</TabPanel>
                    </>)}
                </>)}</TabPanel>
                <TabPanel value={tabValue} index={3} sx={{ pt: 0 }}>{() => (<>
                    {/* Processing: General / Payments / Shipping & pickup / Customer fields.
                        Same pattern as Pages and Sync — sub-tabs hug the main tabs
                        with mx:-3 to extend edge-to-edge. */}
                    <Box sx={subTabBarSx}>
                        <Tabs value={procTab} onChange={(_e, v) => startTransition(() => setProcTab(v))} className={props.theme} sx={subTabsSx}>
                            <Tab label={props.t ? props.t('cfg_proc_tab_general',  'General')          : 'General'}          icon={<SettingsOutlinedIcon />}     iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            {/* Red dot = invalid POS value on that sub-tab (posCheck.js). */}
                            <Tab label={<Badge color="error" variant="dot" invisible={!posIssueCounts.payments} sx={posIssueDotSx}>{props.t ? props.t('cfg_proc_tab_payments', 'Payments') : 'Payments'}</Badge>}         icon={<CreditCardOutlinedIcon />}    iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={<Badge color="error" variant="dot" invisible={!posIssueCounts.shipping} sx={posIssueDotSx}>{props.t ? props.t('cfg_proc_tab_shipping', 'Shipping & pickup') : 'Shipping & pickup'}</Badge>} icon={<LocalShippingOutlinedIcon />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_proc_tab_fields',   'Customer fields')  : 'Customer fields'}  icon={<PersonAddAlt1OutlinedIcon />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                        </Tabs>
                    </Box>
                    {configLoading ? renderConfigSkeleton() : (
                    <ProcessingTab theme={props.theme} t={props.t} subTab={procTab} config={processingConfig} onConfigChange={setProcessingConfig} isSftUser={String(props.login?.sft_status ?? '0') !== '0'} posOptions={posOptions} warehouseOptions={warehouseOptions} salutationOptions={salutationOptions} countryOptions={countryOptions} paymentProviderAvailability={paymentProviderAvailability} paymentTypeOptions={paymentTypeOptions} customerCategoryGroups={customerCategoryGroups} onCustomerCategoryGroupsChange={setCustomerCategoryGroups} stores={stores} onStoresChange={setStores} validateCustomerId={validateCustomerId} onApplyCountryPreset={applyCountryPreset} onEditCountryRates={goToDataCountries} />
                    )}
                </>)}</TabPanel>
                <TabPanel value={tabValue} index={4} sx={{ pt: 0 }}>{() => (<>
                    {/* Sync-settings: General / Field mapping / Images.
                        Same pattern as Pages — sub-tabs hug the main tabs
                        with mx:-3 to extend edge-to-edge. */}
                    <Box sx={subTabBarSx}>
                        <Tabs value={syncTab} onChange={(_e, v) => startTransition(() => setSyncTab(v))} className={props.theme} sx={subTabsSx}>
                            <Tab label={props.t ? props.t('cfg_sync_tab_general', 'General') : 'General'}             icon={<RestartAltIcon />}    iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_sync_tab_field',   'Field mapping') : 'Field mapping'} icon={<HubOutlinedIcon />}   iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                            <Tab label={props.t ? props.t('cfg_sync_tab_images',  'Images') : 'Images'}               icon={<ImageOutlinedIcon />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
                        </Tabs>
                    </Box>
                    {configLoading ? renderConfigSkeleton() : (
                    <SyncSettingsTab theme={props.theme} t={props.t} subTab={syncTab} config={syncConfig} onConfigChange={setSyncConfig} warehouseOptions={warehouseOptions} shopFieldLabels={shopFieldLabels} />
                    )}
                </>)}</TabPanel>
                <TabPanel value={tabValue} index={5}>{() => (<>{renderShortcutsTab()}</>)}</TabPanel>
                </Suspense>

            </Box>

            {/* The floating save button is gone — the persistent save button
                in the header (next to the search field) is the single save
                entry point now. */}

            <Dialog
                open={!!configBlock}
                onClose={closeConfigBlockDialog}
                /* intro/carousel/image-grid/text render rich editors (unlayer,
                   image-upload grid) so they get the wide canvas; other blocks
                   fall back to a compact placeholder form. */
                {...webshopModalSizing(
                    ['intro', 'carousel', 'imagegrid', 'imageGrid', 'text'].includes(configBlock?.id) ? 'wide' : 'compact',
                    { collapsed: sidebarCollapsed, sx: { ...variantTokens.dialog.paperSx, display: 'flex', flexDirection: 'column' } },
                )}
            >
                {/* Chrome + body follow the active design variant, exactly like
                    the Opmaak dialog (see _designVariant.js). */}
                <DialogTitle sx={{ ...variantTokens.dialog.titleSx, pr: 6 }}>
                    <Typography variant="h6" component="span" sx={{ ...variantTokens.dialog.titleTypography, display: 'block' }}>
                        {props.t ? props.t('cfg_block_configure', 'Configure') : 'Configure'} {configBlock ? blockLabel(configBlock) : ''}
                    </Typography>
                </DialogTitle>
                <DialogBody t={props.t} deps={[configBlock?.id, useImageGridV2]}>
                    <Suspense fallback={<Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>}>
                    {configBlock?.id === 'intro' && (
                        <Box sx={{ pt: 1 }}>
                            <StaticPageTab
                                theme={props.theme}
                                t={props.t}
                                pageKey="intro"
                                defaultTitle={blockLabel(configBlock)}
                                defaultDescription="This text appears on the landing page as a kind of welcome message."
                                value={staticPages.intro}
                                onChange={setStaticPage('intro')}
                            />
                        </Box>
                    )}
                    {configBlock?.id === 'carousel' && (
                        <Box sx={{ pt: 1 }}>
                            <CarouselConfigModal
                                value={carouselConfig}
                                onChange={setCarouselConfig}
                                theme={props.theme}
                                t={props.t}
                            />
                        </Box>
                    )}
                    {(configBlock?.id === 'imagegrid' || configBlock?.id === 'imageGrid') && (
                        <Box sx={{ pt: 1 }}>
                            {/* Classic / Visueel switch lives in the dialog's
                                action bar (bottom-left) so it doesn't add empty
                                space at the top. Both editors share the same
                                imageGridConfig, so flipping never loses edits. */}
                            {useImageGridV2 ? (
                                <ImageGridConfigModalV2
                                    value={imageGridConfig}
                                    onChange={setImageGridConfig}
                                    theme={props.theme}
                                    t={props.t}
                                />
                            ) : (
                                <ImageGridConfigModal
                                    value={imageGridConfig}
                                    onChange={setImageGridConfig}
                                    theme={props.theme}
                                    t={props.t}
                                />
                            )}
                        </Box>
                    )}
                    {/* 404 "text" block → real editor for the contents_404 /
                        TEXT_404 row, controlled the same way as Disclaimer/Privacy
                        (value + onChange → staticPages.page404 → staticPagesDirty →
                        saved on the main Save as a /admin/contents/TEXT_404 PATCH). */}
                    {configBlock?.id === 'text' && (
                        <Box sx={{ pt: 1 }}>
                            <StaticPageTab
                                theme={props.theme} t={props.t}
                                pageKey="page404"
                                defaultTitle={blockLabel(configBlock)}
                                defaultDescription="This text appears on the 404 (page not found) page."
                                value={staticPages.page404}
                                onChange={setStaticPage('page404')}
                            />
                        </Box>
                    )}
                    {/* Blocks without a dedicated editor: say so honestly instead
                        of the old unbound mock form (A2.7) — those fields saved
                        nothing and only suggested settings that don't exist. */}
                    {configBlock && !['intro', 'carousel', 'imagegrid', 'imageGrid', 'text'].includes(configBlock.id) && (
                        <Typography variant="body2" sx={{ color: mutedText, py: 2 }}>
                            {lbl('cfg_block_no_settings', 'Dit blok heeft geen eigen instellingen — zichtbaarheid en volgorde regel je op het bord.')}
                        </Typography>
                    )}
                    </Suspense>
                </DialogBody>
                <DialogActions sx={variantTokens.dialog.actionsSx}>
                    {/* Classic / Visueel toggle — only for the image-grid
                        block. `mr: auto` pushes it to the left so Cancel/Save
                        stay right-aligned. */}
                    {(configBlock?.id === 'imagegrid' || configBlock?.id === 'imageGrid') && (
                        <Box sx={{
                            mr: 'auto',
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            p: 0.25, borderRadius: 999,
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            border: '1px solid', borderColor: 'divider',
                        }}>
                            {[
                                { v: false, label: props.t ? props.t('cfg_imagegridv2_toggle_classic', 'Klassiek') : 'Klassiek' },
                                { v: true,  label: props.t ? props.t('cfg_imagegridv2_toggle_visual',  'Visueel')  : 'Visueel'  },
                            ].map(o => (
                                <ButtonBase
                                    key={String(o.v)}
                                    onClick={() => setUseImageGridV2(o.v)}
                                    sx={{
                                        px: 1.5, py: 0.5, borderRadius: 999,
                                        fontSize: '0.72rem', fontWeight: 700,
                                        color:           useImageGridV2 === o.v ? 'primary.main' : 'text.secondary',
                                        backgroundColor: useImageGridV2 === o.v ? '#fff' : 'transparent',
                                        boxShadow:       useImageGridV2 === o.v ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                                    }}
                                >
                                    {o.label}
                                </ButtonBase>
                            ))}
                        </Box>
                    )}
                    <Button onClick={closeConfigBlockDialog}>
                        {props.t ? props.t('cancel', 'Cancel') : 'Cancel'}
                    </Button>
                    <ConfigSaveButton saving={configSaving} onSave={saveConfig} onClose={closeConfigBlockDialog} t={props.t} />
                </DialogActions>
            </Dialog>

            {/* Gated with && so the ~390-line dialog tree isn't rebuilt on every
                screen render while closed (Dialog keeps it out of the DOM, but the
                elements were still created each render). The Unlayer portal below
                already does the same. */}
            {containerModalOpen && (
            <Dialog
                open={containerModalOpen}
                onClose={(_event, reason) => {
                    // When editor is in fullscreen, ESC / backdrop should only exit fullscreen,
                    // not close the whole modal.
                    if (editorFullscreen && (reason === 'escapeKeyDown' || reason === 'backdropClick')) {
                        setEditorFullscreen(false);
                        return;
                    }
                    closeContainerModal();
                }}
                {...webshopModalSizing('wide', { collapsed: sidebarCollapsed, sx: { ...variantTokens.dialog.paperSx, display: 'flex', flexDirection: 'column' } })}
            >
                <DialogTitle sx={variantTokens.dialog.titleSx}>
                    <Typography variant="h6" component="span" sx={{ ...variantTokens.dialog.titleTypography, display: 'block' }}>
                        {editingContainerId ? trs('cfg_container_edit_title', 'Edit product container') : trs('cfg_container_new_title', 'New product container')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: mutedText, display: 'block', ...variantTokens.dialog.subtitleSx }}>
                        {trs('cfg_container_subtitle', 'Configure how a product container displays on the shop front page.')}
                    </Typography>
                </DialogTitle>
                <Divider />
                {/* One column on purpose: the sections hold wide two-column
                    forms and the page-template editor. The ref is the scroll
                    node the fullscreen toggle saves/restores. */}
                <DialogBody scrollRef={dialogContentRef} t={props.t} deps={[editingContainerId]}>
                    <ModalCardFlow sx={{ columns: '1' }}>

                        {/* Section: Basics */}
                        <ConfigCard
                                anchor="cfg-card-container-basics"
                                theme={props.theme}
                                title={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}><InfoOutlinedIcon sx={{ color: Colors.sft_blue, fontSize: 20 }} />{trs('cfg_container_basics', 'Basics')}</Box>}
                            >
                            <Stack spacing={2}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        label={trs('cfg_container_reference', 'Reference')}
                                        size="small"
                                        value={containerData.reference}
                                        onChange={(e) => updateContainerField('reference', e.target.value)}
                                        fullWidth
                                        helperText={trs('cfg_container_reference_help', 'Internal identifier used in logs and exports')}
                                    />
                                    <TextField
                                        label={trs('cfg_container_link', 'Link to discovery/lookup page')}
                                        size="small"
                                        value={containerData.link}
                                        onChange={(e) => updateContainerField('link', e.target.value)}
                                        fullWidth
                                        placeholder="/explore?sort=most_recent"
                                        helperText={trs('cfg_container_link_help', 'Where the button takes visitors')}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 3, pt: 0.5 }}>
                                    <FormControlLabel
                                        control={<Switch color="success" checked={containerData.active} onChange={(e) => updateContainerField('active', e.target.checked)} />}
                                        label={trs('cfg_container_active', 'Active')}
                                    />
                                    <FormControlLabel
                                        control={<Switch color="success" checked={containerData.keepVisible} onChange={(e) => updateContainerField('keepVisible', e.target.checked)} />}
                                        label={trs('cfg_container_keep_visible', 'Keep visible when list is empty')}
                                    />
                                </Box>
                            </Stack>
                        </ConfigCard>

                        {/* Section: Layout */}
                        <ConfigCard
                                anchor="cfg-card-container-layout"
                                theme={props.theme}
                                title={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}><TuneIcon sx={{ color: Colors.sft_blue, fontSize: 20 }} />{trs('cfg_container_layout', 'Layout')}</Box>}
                            >
                            <Stack spacing={2}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                                    <TextField
                                        label={trs('cfg_container_max_articles', 'Max. articles')}
                                        type="number"
                                        size="small"
                                        value={containerData.maxArticles}
                                        onChange={(e) => updateContainerField('maxArticles', parseInt(e.target.value) || 0)}
                                    />
                                    <TextField
                                        label={trs('cfg_container_top_margin', 'Top margin')}
                                        type="number"
                                        size="small"
                                        value={containerData.topMargin}
                                        onChange={(e) => updateContainerField('topMargin', parseInt(e.target.value) || 0)}
                                        InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                    />
                                    <TextField
                                        label={trs('cfg_container_bottom_margin', 'Bottom margin')}
                                        type="number"
                                        size="small"
                                        value={containerData.bottomMargin}
                                        onChange={(e) => updateContainerField('bottomMargin', parseInt(e.target.value) || 0)}
                                        InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                    />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 1, ml: 0.5 }}>
                                        {trs('cfg_container_max_width', 'Max width')}
                                    </Typography>
                                    <SliderField
                                        value={containerData.maxWidth}
                                        min={0}
                                        max={WEBSHOP_MAX_WIDTH_PX}
                                        step={10}
                                        unit="px"
                                        label={trs('cfg_container_max_width', 'Max width')}
                                        onChange={(v) => updateContainerField('maxWidth', v)}
                                    />
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 0.5, ml: 0.5 }}>
                                            {trs('cfg_container_view_method', 'View method')}
                                        </Typography>
                                        <Select
                                            value={containerData.viewMethod}
                                            onChange={(e) => updateContainerField('viewMethod', e.target.value)}
                                            fullWidth
                                            size="small"
                                        >
                                            <MenuItem value="horizontal">{trs('cfg_container_view_horizontal', 'Horizontal list')}</MenuItem>
                                            <MenuItem value="grid">{trs('cfg_container_view_grid', 'Grid')}</MenuItem>
                                        </Select>
                                        {containerData.viewMethod === 'grid' && (
                                            <Box sx={{ mt: 1.5 }}>
                                                <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 0.5, ml: 0.5 }}>
                                                    {trs('cfg_container_grid_columns', 'Products per row')}
                                                </Typography>
                                                <Select
                                                    value={containerData.gridColumns || 4}
                                                    onChange={(e) => updateContainerField('gridColumns', Number(e.target.value))}
                                                    fullWidth
                                                    size="small"
                                                >
                                                    {[1, 2, 3, 4, 5].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                                                </Select>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 0.5, ml: 0.5 }}>
                                            {trs('cfg_container_scroll_method', 'Scroll method')}
                                        </Typography>
                                        <Select
                                            value={containerData.scrollMethod}
                                            onChange={(e) => updateContainerField('scrollMethod', e.target.value)}
                                            fullWidth
                                            size="small"
                                        >
                                            <MenuItem value="arrows_large">{trs('cfg_container_scroll_arrows_large', 'Arrows (large)')}</MenuItem>
                                            <MenuItem value="arrows_small">{trs('cfg_container_scroll_arrows_small', 'Arrows (small)')}</MenuItem>
                                            <MenuItem value="scrollbar">{trs('cfg_container_scroll_scrollbar', 'Scrollbar')}</MenuItem>
                                            <MenuItem value="none">{trs('cfg_container_scroll_none', 'None')}</MenuItem>
                                        </Select>
                                    </Box>
                                </Box>
                            </Stack>
                        </ConfigCard>

                        {/* Section: Content */}
                        <ConfigCard
                                anchor="cfg-card-container-content"
                                theme={props.theme}
                                title={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}><TextFieldsIcon sx={{ color: Colors.sft_blue, fontSize: 20 }} />{trs('cfg_container_content', 'Content')}</Box>}
                            >
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5, color: textColor }}>
                                        {trs('cfg_container_title_label', 'Container title')}
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        {[
                                            { code: 'NL', field: 'titleNL' },
                                            { code: 'EN', field: 'titleEN' },
                                            { code: 'FR', field: 'titleFR' },
                                        ].filter(({ code }) => (styleSettings.visibility.adminLanguages || ALL_LANGUAGE_CODES).includes(code)).map(({ code, field }) => (
                                            <TextField
                                                key={field}
                                                size="small"
                                                value={containerData[field]}
                                                onChange={(e) => updateContainerField(field, e.target.value)}
                                                fullWidth
                                                placeholder={`${trs('cfg_container_title_ph', 'Title')} (${code})`}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Box sx={{
                                                                px: 1,
                                                                py: 0.25,
                                                                fontSize: '0.7rem',
                                                                fontWeight: 700,
                                                                color: mutedText,
                                                                backgroundColor: panelBg,
                                                                borderRadius: 1,
                                                                letterSpacing: 0.5,
                                                            }}>
                                                                {code}
                                                            </Box>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5, color: textColor }}>
                                        {trs('cfg_container_button_text', 'Button text')}
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        {[
                                            { code: 'NL', field: 'buttonNL' },
                                            { code: 'EN', field: 'buttonEN' },
                                            { code: 'FR', field: 'buttonFR' },
                                        ].filter(({ code }) => (styleSettings.visibility.adminLanguages || ALL_LANGUAGE_CODES).includes(code)).map(({ code, field }) => (
                                            <TextField
                                                key={field}
                                                size="small"
                                                value={containerData[field]}
                                                onChange={(e) => updateContainerField(field, e.target.value)}
                                                fullWidth
                                                placeholder={`${trs('cfg_container_button_ph', 'Button')} (${code})`}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Box sx={{
                                                                px: 1,
                                                                py: 0.25,
                                                                fontSize: '0.7rem',
                                                                fontWeight: 700,
                                                                color: mutedText,
                                                                backgroundColor: panelBg,
                                                                borderRadius: 1,
                                                                letterSpacing: 0.5,
                                                            }}>
                                                                {code}
                                                            </Box>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            </Box>
                        </ConfigCard>

                        {/* Section: Styling */}
                        <ConfigCard
                                anchor="cfg-card-container-styling"
                                theme={props.theme}
                                title={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}><PaletteIcon sx={{ color: Colors.sft_blue, fontSize: 20 }} />{trs('cfg_container_styling', 'Styling')}</Box>}
                            >
                            <Stack spacing={3}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, alignItems: 'flex-start' }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 0.5, ml: 0.5 }}>
                                            {trs('cfg_container_title_font_size', 'Title font size')}
                                        </Typography>
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={containerData.titleFontSize}
                                            onChange={(e) => updateContainerField('titleFontSize', parseFloat(e.target.value) || 0)}
                                            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                            fullWidth
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 0.5, ml: 0.5 }}>
                                            {trs('cfg_container_title_position', 'Title position')}
                                        </Typography>
                                        <ToggleButtonGroup
                                            value={containerData.titlePosition}
                                            exclusive
                                            onChange={(_e, v) => v && updateContainerField('titlePosition', v)}
                                            size="small"
                                            fullWidth
                                            sx={{ '& .MuiToggleButton-root': { height: 43, py: 0 } }}
                                        >
                                            <ToggleButton value="left"><FormatAlignLeftIcon /></ToggleButton>
                                            <ToggleButton value="center"><FormatAlignCenterIcon /></ToggleButton>
                                            <ToggleButton value="right"><FormatAlignRightIcon /></ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 1, ml: 0.5 }}>
                                        {trs('cfg_container_button_color', 'Button text color')}
                                    </Typography>
                                    <Stack direction="row" spacing={1.5}>
                                        {[
                                            { value: 'primary', label: trs('cfg_container_color_primary', 'Primary color'), color: Colors.sft_blue },
                                            { value: 'secondary', label: trs('cfg_container_color_secondary', 'Secondary color'), color: '#9acfbf' },
                                        ].map(({ value, label, color }) => {
                                            const isSelected = containerData.buttonColor === value;
                                            return (
                                                <Box
                                                    key={value}
                                                    onClick={() => updateContainerField('buttonColor', value)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.25,
                                                        cursor: 'pointer',
                                                        px: 2,
                                                        py: 1,
                                                        borderRadius: 1.5,
                                                        border: `2px solid ${isSelected ? Colors.sft_blue : borderColor}`,
                                                        backgroundColor: isSelected ? (isDark ? 'rgba(25,118,210,0.12)' : 'rgba(25,118,210,0.08)') : 'transparent',
                                                        transition: 'all 150ms',
                                                        '&:hover': {
                                                            backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                            borderRadius: '50%',
                                                            backgroundColor: color,
                                                            border: `1px solid ${borderColor}`,
                                                        }}
                                                    />
                                                    <Typography variant="body2" sx={{ color: isSelected ? Colors.sft_blue : textColor, fontWeight: isSelected ? 600 : 400 }}>
                                                        {label}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            </Stack>
                        </ConfigCard>

                        {/* Section: Page Template (Unlayer editor) — header + inline slot.
                            Actual content is rendered via portal below, so it can be moved to
                            document.body when fullscreen without unmounting the editors. */}
                        <ConfigCard
                            anchor="cfg-card-container-page-template"
                            theme={props.theme}
                            title={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}><WebIcon sx={{ color: Colors.sft_blue, fontSize: 20 }} />{trs('cfg_container_page_template', 'Page template')}</Box>}
                            headerActions={(containerData.editor || 'unlayer') === 'unlayer' ? (
                                <IconButton
                                    size="small"
                                    onClick={toggleEditorFullscreen}
                                    title={editorFullscreen ? trs('cfg_container_exit_fullscreen', 'Exit fullscreen') : trs('cfg_container_fullscreen', 'Fullscreen')}
                                >
                                    {editorFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                </IconButton>
                            ) : undefined}
                        >
                            {/* Storage form of the body: text, designer or own HTML
                                (as in the old admin). Not while the stored text is
                                unknown — a switch has to take it along. */}
                            <Box sx={{ mb: 2 }}>
                                <EditorModeSwitch
                                    value={containerData.editor || 'unlayer'}
                                    onChange={switchContainerEditor}
                                    t={props.t}
                                    busy={containerModeBusy}
                                    disabled={containerBodyStatus !== 'ok'}
                                />
                                {containerBodyStatus !== 'ok' && (containerData.editor || 'unlayer') === 'unlayer' && (
                                    <Typography variant="caption" sx={{ display: 'block', color: mutedText, mt: 0.75 }}>
                                        {containerBodyStatus === 'loading'
                                            ? trs('ui_content_loading', 'Loading the current text…')
                                            : trs('ui_content_switch_unavailable', 'Switching to text or own HTML is not possible: the current content could not be loaded.')}
                                    </Typography>
                                )}
                            </Box>
                            {(containerData.editor || 'unlayer') === 'unlayer' ? (
                                /* Portal target for the editor UI when not fullscreen. */
                                <Box
                                    ref={setEditorInlineSlot}
                                    sx={{ minHeight: editorFullscreen ? 0 : 700 }}
                                />
                            ) : (
                                <Box>
                                    <FormControlLabel
                                        sx={{ mb: 1.5 }}
                                        control={
                                            <Switch color="success"
                                                checked={containerData.useIframe}
                                                onChange={(e) => updateContainerField('useIframe', e.target.checked)}
                                            />
                                        }
                                        label={trs('cfg_container_iframe', 'Render in iframe (recommended when using advanced styling or scripts)')}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                                        <Tabs value={editorLang} onChange={(_e, v) => setEditorLang(v)} sx={{ minHeight: 36 }}>
                                            {containerLangIdx.map(i => (
                                                <Tab key={i} value={i} label={CONTAINER_LANG_LABELS[i]} sx={{ minHeight: 36, textTransform: 'none' }} />
                                            ))}
                                        </Tabs>
                                        {containerBodyStatus === 'ok' && (
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography variant="caption" sx={{ color: mutedText }}>
                                                    {trs('cfg_container_copy_text_from', 'Copy text from:')}
                                                </Typography>
                                                {containerLangIdx.filter(i => i !== editorLang).map(i => CONTAINER_LANGS[i]).map((U) => (
                                                    <Button
                                                        key={U} size="small" variant="outlined"
                                                        onClick={() => updateContainerField(`text${['NL', 'EN', 'FR'][editorLang]}`, containerData[`text${U}`] || '')}
                                                    >{U}</Button>
                                                ))}
                                            </Stack>
                                        )}
                                    </Box>
                                    {containerBodyStatus !== 'ok' ? (
                                        <ContentBodyNotice
                                            status={containerBodyStatus}
                                            onRetry={() => { const id = editingBlockDbId(); if (id != null) loadContainerBody(id); }}
                                            t={props.t}
                                        />
                                    ) : (
                                        <TextBodyEditor
                                            mode={containerData.editor}
                                            value={containerData[`text${['NL', 'EN', 'FR'][editorLang]}`] || ''}
                                            onChange={((U) => (val) => updateContainerField(`text${U}`, val))(['NL', 'EN', 'FR'][editorLang])}
                                            docKey={`${editingContainerId || 'new'}:${editorLang}`}
                                            t={props.t}
                                        />
                                    )}
                                </Box>
                            )}
                        </ConfigCard>
                    </ModalCardFlow>
                </DialogBody>
                <Divider />
                <DialogActions sx={variantTokens.dialog.actionsSx}>
                    <Button onClick={closeContainerModal}>{trs('cancel', 'Cancel')}</Button>
                    <Button variant="contained" onClick={saveContainer}>
                        {editingContainerId ? trs('cfg_container_save', 'Save changes') : trs('cfg_container_create', 'Create container')}
                    </Button>
                </DialogActions>
            </Dialog>
            )}

            {/* Editor content always lives in a portal to document.body. The DOM never
                moves — only CSS coordinates change. This keeps the Unlayer iframe mounted
                across fullscreen toggles so user edits are preserved. When not fullscreen,
                the overlay follows the inline slot's bounding rect; when fullscreen, it
                covers the viewport. */}
            {containerModalOpen && (containerData.editor || 'unlayer') === 'unlayer' && (editorFullscreen || slotRect) && createPortal(
                (() => {
                    // Compute clipping region so the overlay is only visible inside
                    // the Dialog's viewport area when inline.
                    let clipTop = 0;
                    let clipBottom = 0;
                    if (!editorFullscreen && slotRect) {
                        clipTop = Math.max(0, slotRect.dialogTop - slotRect.top);
                        const overlayBottom = slotRect.top + Math.max(slotRect.height, 0);
                        clipBottom = Math.max(0, overlayBottom - slotRect.dialogBottom);
                    }
                    return (
                        <Box
                            sx={editorFullscreen ? {
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                zIndex: 2000,
                                backgroundColor: cardBg,
                                p: 3,
                                overflow: 'auto',
                            } : {
                                position: 'fixed',
                                top: slotRect?.top,
                                left: slotRect?.left,
                                width: slotRect?.width,
                                minHeight: slotRect?.height,
                                zIndex: 1400, // above Dialog Paper (default 1300)
                                pointerEvents: 'auto',
                                clipPath: `inset(${clipTop}px 0px ${clipBottom}px 0px)`,
                                WebkitClipPath: `inset(${clipTop}px 0px ${clipBottom}px 0px)`,
                            }}
                        >
                    {editorFullscreen && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <WebIcon sx={{ color: Colors.sft_blue, fontSize: 20 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {trs('cfg_container_page_template', 'Page template')}
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            <IconButton size="small" onClick={toggleEditorFullscreen} title={trs('cfg_container_exit_fullscreen', 'Exit fullscreen')}>
                                <FullscreenExitIcon />
                            </IconButton>
                        </Stack>
                    )}

                    {!editorFullscreen && (
                        <FormControlLabel
                            sx={{ mb: 2 }}
                            control={
                                <Switch color="success"
                                    checked={containerData.useIframe}
                                    onChange={(e) => updateContainerField('useIframe', e.target.checked)}
                                />
                            }
                            label={trs('cfg_container_iframe', 'Render in iframe (recommended when using advanced styling or scripts)')}
                        />
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                        <Tabs
                            value={editorLang}
                            onChange={(_e, v) => setEditorLang(v)}
                            sx={{ minHeight: 36 }}
                        >
                            {containerLangIdx.map(i => (
                                <Tab key={i} value={i} label={CONTAINER_LANG_LABELS[i]} sx={{ minHeight: 36, textTransform: 'none' }} />
                            ))}
                        </Tabs>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <ShopDocumentImport
                                getEditor={() => getEditorRef(['NL', 'EN', 'FR'][editorLang])?.current?.editor}
                                t={props.t}
                            />
                            <Typography variant="caption" sx={{ color: mutedText }}>
                                {trs('cfg_container_copy_from', 'Copy layout from:')}
                            </Typography>
                            {containerLangIdx.filter(i => i !== editorLang).map(i => CONTAINER_LANGS[i]).map(from => (
                                <Button key={from} size="small" variant="outlined" onClick={() => copyEditorDesign(from, CONTAINER_LANGS[editorLang])}>{from}</Button>
                            ))}
                        </Stack>
                    </Box>

                    <Box sx={{ display: editorLang === 0 ? 'block' : 'none' }}>
                        <UnlayerEditor
                            ref={editorRefNL}
                            minHeight={editorFullscreen ? window.innerHeight - 180 : 600}
                            displayMode="web"
                            onReady={onEditorReady('NL')}
                            appearance={{
                                theme: props.theme,
                                panels: { tools: { dock: 'right' } },
                            }}
                            options={{
                                displayMode: 'web',
                                features: {
                                    preview: false, // DIAGNOSTIC: should hide preview icons if features is read
                                    textEditor: {
                                        cleanPaste: false, // keep formatting when pasting
                                                                        customButtons: [
                                    {
                                        name: 'my_button', // Unique identifier for the button
                                        text: 'My Button', // The label that will appear on the button
                                        icon: 'bookmark', // Icon that will appear on the button (optional)
                                        onSetup: () => {}, // Function executed when the button is set up
                                        onAction: (data, callback) => {
                                            callback(data.text + ' Updated'); // Perform action and update the text
                                        },
                                    },
                                ],
                                    },
                                    
                                },
                            }}
                        />
                    </Box>
                    <Box sx={{ display: editorLang === 1 ? 'block' : 'none' }}>
                        <UnlayerEditor
                            ref={editorRefEN}
                            minHeight={editorFullscreen ? window.innerHeight - 180 : 600}
                            displayMode="web"
                            onReady={onEditorReady('EN')}
                            appearance={{
                                theme: props.theme,
                                panels: { tools: { dock: 'right' } },
                            }}
                            options={{
                                displayMode: 'web',
                                features: {
                                    preview: false, // DIAGNOSTIC: should hide preview icons if features is read
                                    textEditor: {
                                        cleanPaste: false, // keep formatting when pasting
                                                                        customButtons: [
                                    {
                                        name: 'my_button', // Unique identifier for the button
                                        text: 'My Button', // The label that will appear on the button
                                        icon: 'bookmark', // Icon that will appear on the button (optional)
                                        onSetup: () => {}, // Function executed when the button is set up
                                        onAction: (data, callback) => {
                                            callback(data.text + ' Updated'); // Perform action and update the text
                                        },
                                    },
                                ],
                                    },
                                },
                            }}
                        />
                    </Box>
                    <Box sx={{ display: editorLang === 2 ? 'block' : 'none' }}>
                        <UnlayerEditor
                            ref={editorRefFR}
                            minHeight={editorFullscreen ? window.innerHeight - 180 : 600}
                            displayMode="web"
                            onReady={onEditorReady('FR')}
                            appearance={{
                                theme: props.theme,
                                panels: { tools: { dock: 'right' } },
                            }}
                            options={{
                                displayMode: 'web',
                                features: {
                                    preview: false, // DIAGNOSTIC: should hide preview icons if features is read
                                    textEditor: {
                                        cleanPaste: false, // keep formatting when pasting
                                                                        customButtons: [
                                    {
                                        name: 'my_button', // Unique identifier for the button
                                        text: 'My Button', // The label that will appear on the button
                                        icon: 'bookmark', // Icon that will appear on the button (optional)
                                        onSetup: () => {}, // Function executed when the button is set up
                                        onAction: (data, callback) => {
                                            callback(data.text + ' Updated'); // Perform action and update the text
                                        },
                                    },
                                ],
                                    },
                                },
                            }}
                        />
                    </Box>
                </Box>
                    );
                })(),
                document.body
            )}
        </div>
        </ShopDocumentImportProvider>
        </LanguagesProvider>
    );
};

export default WebshopConfigScreen;
